import React, { useEffect, useRef, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ComponentConfig,
  LayoutTypes,
  ComponentType,
  FlexDirection,
  WidthMode,
  HeightMode,
  ValidationRule,
  FieldVisibilityRule,
  FormComponent,
} from "../services/types/ComponentTypes";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import {
  fetchLookup,
  fetchDependentLookup,
  fetchSubModuleRawData,
  addLookupItem,
  clearAllLookups,
} from "../state/slices/lookupsSlice";
import { navigateToView } from "../state/slices/navigationSlice";
import { deriveScreenId, getScreenApiName } from "../state/utils/configUtils";
import { runConditionalValidations } from "../state/utils/conditionalValidationUtils";
import { showConfirmationDialog } from "../state/slices/confirmationDialogSlice";

// Layout Components

// Form Field Components
import { TextField } from "./FormFields/TextField";
import { TextArea } from "./FormFields/TextArea";
import { RadioField } from "./FormFields/RadioField";
import { SelectDropdown } from "./FormFields/SelectDropdown";
import { SearchableDropdown } from "./FormFields/SearchableDropdown";
import { DateField } from "./FormFields/DateField";
import { DateTimeField } from "./FormFields/DateTimeField";
import { ImageUpload } from "./FormFields/ImageUpload";
import { FileUpload } from "./FormFields/FileUpload";
import { Button } from "./FormFields/Layout/Button";
import { InfoCard } from "./FormFields/Layout/InfoCard";
import { NavItem } from "./FormFields/NavItem";
import { CollapsibleNavItem } from "./FormFields/CollapsibleNavItem";
import {
  DataTable,
  DataTableFetchParams,
  DataTableFetchResponse,
} from "./FormFields/DataTable";
import { UnlockModal } from "./UnlockModal";
import WebView from "./FormFields/WebView";
import { SubModule } from "./FormFields/SubModule";
import { MultiSubList } from "./FormFields/MultiSubList";
import { Checkbox } from "./FormFields/Checkbox";
import { MultiCheckBox } from "./FormFields/MultiCheckBox";
import { Report } from "./FormFields/Report";
import LeaveBalance from "./FormFields/LeaveBalance";
import CheckInOut from "./FormFields/CheckInOut";
import HrmStatCards from "./FormFields/HrmStatCards";
import InfoCardsWithStats from "./FormFields/InfoCardsWithStats";
import DateFilter from "./FormFields/DateFilter";
import MonthFilter from "./FormFields/MonthFilter";
import { apiService, LookupOption, GenericModuleResponse } from "../services/api";
import { isComponentVisible } from "../utils/visibilityUtils";
import {
  SummaryListComponent,
  ScreenGroupComponent,
  FabAction,
  SelectDropdownComponent,
  SearchableDropdownComponent,
  ButtonComponent,
  ProfileHeaderCardComponent,
  ProfileContactInfoComponent,
  ProfileDepartmentInfoComponent,
  ProfileTabsComponent,
} from "../services/types/ComponentTypes";

import ProfileHeaderCard from "./FormFields/Profile/ProfileHeaderCard";
import ProfileContactInfo from "./FormFields/Profile/ProfileContactInfo";
import ProfileDepartmentInfo from "./FormFields/Profile/ProfileDepartmentInfo";
import ProfileTabs from "./FormFields/Profile/ProfileTabs";
import { ProfileAddTabs } from "./FormFields/ProfileAddTabs";
interface ComponentRendererProps {
  config: ComponentConfig[];
  formData?: Record<string, any>;
  onFormDataChange?: (name: string, value: any) => void;
  onActionTrigger?: (actionId: string, data?: any) => void;
  expandedItems?: Set<string>;
  onToggleExpanded?: (itemName: string) => void;
  refreshKey?: string | number; // Key to force DataTable refresh
  activeView?: string; // Current active view/action for highlighting menu items
  activeRoute?: string; // Current active route for highlighting menu items
  /** When set, Redux form/edit/loading keys use this id instead of navigation-derived screen id (e.g. drawer). */
  screenContextId?: string;
  /** When "popup", avoid portaling fixed buttons to document.body. */
  renderMode?: "page" | "popup";
}

export const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  config,
  formData = {},
  onFormDataChange,
  onActionTrigger,
  expandedItems,
  activeView,
  activeRoute,
  onToggleExpanded,
  refreshKey,
  screenContextId,
  renderMode = "page",
}) => {
  const dispatch = useAppDispatch();
  const previousView = useAppSelector((state) => state.navigation.previousView);
  const currentView = useAppSelector((state) => state.navigation.currentView);
  const screenId =
    screenContextId || deriveScreenId(currentView) || currentView;
  const editModeInfo = useAppSelector(
    (state) => state.forms.editModeByScreen[screenId]
  );
  const isEditMode = editModeInfo?.isEditMode || false;
  const isLoading = useAppSelector(
    (state) => state.forms.loadingByScreen[screenId] || false
  );
  const isSaving = useAppSelector(
    (state) => state.forms.savingByScreen[screenId] || false
  );
  const user = useAppSelector((state) => state.auth.user);

  // Platform identifier for component visibility (web/mobile/tab)
  const PLATFORM_WEB = "web";

  /**
   * Checks if a component should be visible on the current platform
   * based on its optional `componentVisiblity` setting.
   *
   * Rules:
   * - If `componentVisiblity` is not present or not an array -> visible (backwards compatible)
   * - If present and includes "web" -> visible
   * - If present and does not include "web" -> hidden
   */
  const isComponentVisibleOnPlatform = (comp: ComponentConfig | any): boolean => {
    const visibility = comp?.componentVisiblity;
    if (!Array.isArray(visibility)) {
      return true;
    }
    return visibility.includes(PLATFORM_WEB);
  };

  // Screen Locking State
  const [isScreenLocked, setIsScreenLocked] = useState<boolean>(false);
  const [showUnlockModal, setShowUnlockModal] = useState<boolean>(false);
  const [isUnlocking, setIsUnlocking] = useState<boolean>(false);
  const lockInitializedRef = useRef<string | null>(null);

  // Helper function to check if config has a lock button
  const hasLockButton = (comps: ComponentConfig[]): boolean => {
    for (const comp of comps) {
      // Check for button with name 'lock' or action 'unlock'/'lock'
      if (
        comp.componentType === ComponentType.BUTTON &&
        ((comp.name && comp.name.toLowerCase() === "lock") ||
          (comp.onPressAction && (comp.onPressAction.toLowerCase() === "unlock" || comp.onPressAction.toLowerCase() === "lock")))
      ) {
        return true;
      }

      // Check for formActions inside FORM component
      if (comp.componentType === LayoutTypes.FORM && (comp as any).formActions) {
        const actions = (comp as any).formActions;
        if (Array.isArray(actions)) {
          for (const action of actions) {
            if (
              (action.name && action.name.toLowerCase() === "lock") ||
              (action.type && action.type.toLowerCase() === "lock") ||
              (action.onPressAction && (action.onPressAction.toLowerCase() === "unlock" || action.onPressAction.toLowerCase() === "lock"))
            ) {
              return true;
            }
          }
        }
      }

      // Recursively check children
      if ((comp as any).Fields && Array.isArray((comp as any).Fields)) {
        if (hasLockButton((comp as any).Fields)) return true;
      }
      if (comp.childComponents && Array.isArray(comp.childComponents)) {
        if (hasLockButton(comp.childComponents)) return true;
      }
      if ((comp as any).childControls && Array.isArray((comp as any).childControls)) {
        if (hasLockButton((comp as any).childControls)) return true;
      }
    }
    return false;
  };

  // Initialize lock state based on Edit Mode AND presence of lock button
  useEffect(() => {
    const itemId = formData?.id || formData?._id || formData?.recordId || "new";
    const cacheKey = `${screenId}_${itemId}`;

      if (isEditMode && lockInitializedRef.current !== cacheKey) {
        // Determine if the screen has a lock button; if so, always start locked
        const hasLock = hasLockButton(config);
        const shouldLock = hasLock ? true : false;
        setIsScreenLocked(shouldLock);
        lockInitializedRef.current = cacheKey;
      } else if (!isEditMode) {
        // Never lock in ADD mode
        setIsScreenLocked(false);
        lockInitializedRef.current = null;
      }
  }, [isEditMode, config, formData, screenId]);

  const handleLockToggle = () => {
    if (isScreenLocked) {
      // If currently locked, show modal to unlock
      setShowUnlockModal(true);
    } else {
      // If unlocked, lock via API
      lockScreen();
    }
  };

  const handleUnlockSubmit = async (remarks: string) => {
    setIsUnlocking(true);
    try {
      // Derive module name from screenId or config
      // Usually screenId is like 'add_estimate' or '116_add_estimate'
      // The API expects 'estimation_add' based on user example, but that might be specific.
      // We will search for a FORM component to get its 'Name' as the module name, or fallback to screenId.
      let moduleName = screenId;
      const findFormName = (comps: ComponentConfig[]): string | undefined => {
        for (const comp of comps) {
          if (comp.componentType === LayoutTypes.FORM && (comp as any).Name) {
            return (comp as any).Name;
          }
          if (comp.childComponents) {
            const found = findFormName(comp.childComponents);
            if (found) return found;
          }
        }
        return undefined;
      };
      const formName = findFormName(config);
      if (formName) moduleName = formName;

      // Get Item ID (usually from formData.id or similar)
      const itemId = formData.id || formData._id || formData.recordId;

      if (!itemId) {
        console.error("Cannot unlock: No item ID found in formData");
        // Fallback to strict toggle if no ID (e.g. testing) catch will handle it?
        // Let's proceed to try API anyway, maybe it fails but we handled it.
      }

      // Prepare payload
      // We need updated_by, let's try to get it from auth slice state in Redux if possible,
      // but we can't easily access store here without selector.
      // We'll rely on global store access or just omit if not critical (backend might handle it from token).
      // Actually, we can use useAppSelector for auth.
      // ADDING Auth selector:
      // const user = useAppSelector((state) => state.auth.user); - need to add this to component body

      const payload = {
        itemid: itemId,
        module: moduleName,
        locked: "0",
        remarks: remarks,
        updated_by: user?.id || "",
      };

      await apiService.put(`/pabws/lock/${moduleName}`, payload);

      // On success
      setIsScreenLocked(false);
      setShowUnlockModal(false);
    } catch (error) {
      console.error("Unlock failed:", error);
      dispatch(
        showConfirmationDialog({
          message: "Failed to unlock screen. Please try again.",
          title: "Error",
          resolve: () => { },
        })
      );
    } finally {
      setIsUnlocking(false);
    }
  };

  // API call to lock the screen
  const lockScreen = async () => {
    try {
      // Derive module name similar to unlock flow
      let moduleName = screenId;
      const findFormName = (comps: ComponentConfig[]): string | undefined => {
        for (const comp of comps) {
          if (comp.componentType === LayoutTypes.FORM && (comp as any).Name) {
            return (comp as any).Name;
          }
          if (comp.childComponents) {
            const found = findFormName(comp.childComponents);
            if (found) return found;
          }
        }
        return undefined;
      };
      const formName = findFormName(config);
      if (formName) moduleName = formName;
      const itemId = formData.id || formData._id || formData.recordId;
      const payload = {
        itemid: itemId,
        module: moduleName,
        locked: "1",
        remarks: "Locked via UI",
        updated_by: user?.id || "",
      };
      await apiService.put(`/pabws/lock/${moduleName}`, payload);
      setIsScreenLocked(true);
    } catch (error) {
      console.error("Lock failed:", error);
      dispatch(
        showConfirmationDialog({
          message: "Failed to lock screen. Please try again.",
          title: "Error",
          resolve: () => {},
        })
      );
    }
  };
  // Track previous parent field values to detect user-initiated changes
  const previousParentValuesRef = useRef<Record<string, any>>({});
  // Track if we're in the initial load phase (to prevent auto-populate during initial load in edit mode)
  const isInitialLoadRef = useRef<boolean>(true);
  // Cache dataFetcher functions to prevent recreation on every render
  const dataFetcherCacheRef = useRef<
    Map<
      string,
      (params: DataTableFetchParams) => Promise<DataTableFetchResponse>
    >
  >(new Map());
  const previousViewRef = useRef<string>(currentView);
  // Track if default values have been initialized for this screen
  const defaultValuesInitializedRef = useRef<string | null>(null);
  // Track if we're currently initializing default values (to prevent dependent lookups from fetching during initialization)
  const isInitializingDefaultsRef = useRef<boolean>(false);
  // Track which lookups have been requested to prevent duplicate requests
  const requestedLookupsRef = useRef<Set<string>>(new Set());
  // Prevent repeated vendor auto-resolution loops for screen 130 edit prefill.
  const vendorResolutionRef = useRef<string>("");
  // Track previous parent values string to detect actual changes
  const previousParentValuesStringRef = useRef<string>("");
  // Track calculated ExpressionV2 values to prevent infinite update loops
  const previousCalculatedValuesRef = useRef<Map<string, string | number>>(new Map());


  /** Convert time string (HH:mm or HH:mm:ss) to decimal hours for ExpressionV2 duration math (e.g. time shift). */
  const timeStringToDecimalHours = (val: any): number | null => {
    if (val === undefined || val === null || val === "") return null;
    const s = String(val).trim();
    const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = match[3] ? parseInt(match[3], 10) : 0;
    return hours + minutes / 60 + seconds / 3600;
  };

  // Arrow key navigation for form fields
  useEffect(() => {
    const handleArrowKeyNavigation = (e: KeyboardEvent) => {
      // Only handle arrow keys when focus is on a form field
      const target = e.target as HTMLElement;
      if (
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.closest('input, textarea, select, [role="combobox"], [role="textbox"]')
        )
      ) {
        return;
      }

      // Don't interfere with text selection or if modifier keys are pressed
      if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }

      // Check if this is a searchable dropdown - don't interfere with arrow keys at all
      // This check must come BEFORE the cursor boundary check
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        // Check if event was marked as handled by searchable dropdown
        if ((e as any).__searchableDropdownHandled) {
          return;
        }
        // Check if target is a searchable dropdown input
        if (target instanceof HTMLInputElement && target.getAttribute('data-searchable-dropdown') === 'true') {
          return; // Let searchable dropdown handle arrow keys (both when open and closed)
        }
        // Also check if target is inside a searchable dropdown container
        const searchableDropdownInput = target.closest('[data-field-name]')?.querySelector('input[data-searchable-dropdown="true"]') as HTMLInputElement;
        if (searchableDropdownInput) {
          return; // Let searchable dropdown handle arrow keys
        }
      }

      // For dropdowns (select elements), don't interfere with their native arrow key navigation
      if (target instanceof HTMLSelectElement) {
        return; // Let native select handle arrow keys
      }

      // Check if dropdown is open (for custom dropdowns like SelectDropdown)
      // This includes both searchable dropdowns and regular select dropdowns
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const dropdown = target.closest('[role="combobox"]');
        if (dropdown) {
          const isOpen = dropdown.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            return; // Let dropdown handle arrow keys when open
          }
        }
      }

      // For text inputs and textareas, only navigate if cursor is at boundary
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        const input = target as HTMLInputElement | HTMLTextAreaElement;
        const cursorPos = input.selectionStart || 0;
        const textLength = input.value.length;
        const hasSelection = input.selectionStart !== input.selectionEnd;

        // If there's text selection, don't navigate (let user edit)
        if (hasSelection) {
          return;
        }

        // Only navigate if cursor is at the boundary
        const isAtStart = cursorPos === 0;
        const isAtEnd = cursorPos === textLength;

        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          if (!isAtStart) {
            return; // Let browser handle cursor movement
          }
        } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          if (!isAtEnd) {
            return; // Let browser handle cursor movement
          }
        }
      }

      // Handle arrow keys for navigation
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        // Find all focusable form fields in the current form
        const formContainer = target.closest('[data-form-container="true"]') || document.body;
        const focusableFields = Array.from(
          formContainer.querySelectorAll<HTMLElement>(
            'input:not([type="hidden"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled]), [role="combobox"]:not([disabled]), [role="textbox"]:not([readonly]), [tabindex="0"]:not([disabled])'
          )
        ).filter((field) => {
          // Exclude fields that are not visible
          const rect = field.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
          // Also exclude fields with tabIndex="-1"
        }).filter((field) => {
          const tabIndex = field.getAttribute('tabindex');
          return tabIndex !== '-1';
        });

        if (focusableFields.length === 0) return;

        const currentIndex = focusableFields.indexOf(target as HTMLElement);
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;

        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          // Move to next field
          nextIndex = (currentIndex + 1) % focusableFields.length;
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          // Move to previous field
          nextIndex = currentIndex === 0 ? focusableFields.length - 1 : currentIndex - 1;
        }

        if (nextIndex !== currentIndex) {
          e.preventDefault();
          e.stopPropagation();
          focusableFields[nextIndex]?.focus();
          // For inputs, move cursor to end when focusing
          if (focusableFields[nextIndex] instanceof HTMLInputElement ||
            focusableFields[nextIndex] instanceof HTMLTextAreaElement) {
            const nextField = focusableFields[nextIndex] as HTMLInputElement | HTMLTextAreaElement;
            setTimeout(() => {
              nextField.setSelectionRange(nextField.value.length, nextField.value.length);
            }, 0);
          }
        }
      }
    };

    window.addEventListener("keydown", handleArrowKeyNavigation);
    return () => window.removeEventListener("keydown", handleArrowKeyNavigation);
  }, []);

  // Helper function to get default value from field config (checks both Default_Value and defaultValue)
  const getDefaultValue = (fieldConfig: any): any => {
    // Check if this is a date field and DefaultToday is true
    if ((fieldConfig?.componentType === ComponentType.DATE_FIELD || fieldConfig?.componentType === ComponentType.DATETIME_FIELD) && fieldConfig?.DefaultToday) {
      // Return ISO 8601 UTC string for both date and datetime fields
      return new Date().toISOString();
    }

    return fieldConfig?.Default_Value !== undefined
      ? fieldConfig.Default_Value
      : fieldConfig?.defaultValue;
  };

  // Initialize default values in formData when form first loads (only in add mode)
  useEffect(() => {
    // Only initialize defaults in add mode (not edit mode) and if not already initialized for this screen
    if (
      !isEditMode &&
      !isLoading &&
      defaultValuesInitializedRef.current !== screenId
    ) {
      const defaultsToSet: Record<string, any> = {};

      // Recursively walk through all components to find fields with default values
      const collectDefaults = (components: ComponentConfig[] | any[]) => {
        components.forEach((comp: ComponentConfig | any) => {
          // Check if this is a form field with a default value
          if (
            (comp.componentType === ComponentType.TEXT_FIELD ||
              comp.componentType === ComponentType.TEXT_AREA ||
              comp.componentType === ComponentType.DATE_FIELD ||
              comp.componentType === ComponentType.DATETIME_FIELD ||
              comp.componentType === ComponentType.RADIO_FIELD ||
              comp.componentType === ComponentType.SELECT_DROPDOWN ||
              comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
              comp.componentType === ComponentType.MULTI_CHECKBOX) &&
            comp
          ) {
            const fieldName = comp.name || comp.BindValue;
            const defaultValue = getDefaultValue(comp);

            if (
              fieldName &&
              defaultValue !== undefined &&
              defaultValue !== null &&
              defaultValue !== ""
            ) {
              // Only set if not already in formData
              if (
                formData[fieldName] === undefined ||
                formData[fieldName] === null ||
                formData[fieldName] === ""
              ) {
                let finalDefaultValue = defaultValue;

                // CRITICAL: If default value is a label (like "5%") and this is a lookup dropdown,
                // convert it to the ID before storing
                if (
                  (comp.componentType === ComponentType.SELECT_DROPDOWN ||
                    comp.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
                  comp.lookupName
                ) {
                  const lookupName = comp.lookupName || comp.LookupName;
                  if (lookupName) {
                    const lookupKey = lookupName;
                    const lookupOptions = lookupsState.lookups[lookupKey] || [];

                    // Check if the default value matches a label instead of an ID
                    const matchingOption = lookupOptions.find(
                      (opt: any) =>
                        opt.label === defaultValue ||
                        String(opt.label) === String(defaultValue) ||
                        (opt as any).name === defaultValue ||
                        String((opt as any).name) === String(defaultValue)
                    );

                    if (matchingOption) {
                      // Convert label to ID
                      finalDefaultValue =
                        matchingOption.value || (matchingOption as any).id || defaultValue;


                    }
                  }
                }

                defaultsToSet[fieldName] = finalDefaultValue;
              }
            }
          }

          // Recursively process nested components
          if ((comp as any).Fields && Array.isArray((comp as any).Fields)) {
            collectDefaults((comp as any).Fields);
          }
          if (comp.childComponents && Array.isArray(comp.childComponents)) {
            collectDefaults(comp.childComponents);
          }
        });
      };

      collectDefaults(config);

      // Set all default values at once
      if (Object.keys(defaultsToSet).length > 0) {
        if (onFormDataChange) {
          // Set flag to prevent dependent lookup effect from running during initialization
          isInitializingDefaultsRef.current = true;

          // Note: Each onFormDataChange call will trigger a formData update.
          // The dependent lookup effect will check isInitializingDefaultsRef to skip fetching during this phase.
          Object.entries(defaultsToSet).forEach(([fieldName, value]) => {
            onFormDataChange(fieldName, value);
          });

          // Clear the flag after a brief delay to allow all updates to complete
          // This prevents dependent lookups from fetching during default value initialization
          setTimeout(() => {
            isInitializingDefaultsRef.current = false;
          }, 0);


        }
      }

      // Mark as initialized for this screen
      defaultValuesInitializedRef.current = screenId;
    } else if (isEditMode || isLoading) {
      // Reset when entering edit mode or when loading
      defaultValuesInitializedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isLoading, screenId, config]);

  // Clear cache when view changes to prevent using wrong module names
  useEffect(() => {
    if (previousViewRef.current !== currentView) {
      dataFetcherCacheRef.current.clear();
      // Reset default values initialization when view changes
      defaultValuesInitializedRef.current = null;
      // Clear requested lookups when view changes
      requestedLookupsRef.current.clear();
      previousParentValuesStringRef.current = "";
      previousViewRef.current = currentView;

    }
  }, [currentView, screenId]);

  // NOTE: We previously made a fire-and-forget call to GET /pabws/screen/{name}
  // on every screen load using getScreenApiName(screenId, config, currentView).
  // This is now disabled because the backend only requires the
  // \"get screen with id\" API, and we want to avoid this extra call
  // (e.g. /pabws/screen/receipt_summary) when navigating between screens.

  // Helper function to extract all lookup names from config
  const extractLookupNames = (components: ComponentConfig[]): string[] => {
    const lookupNames: string[] = [];
    const processComponent = (comp: ComponentConfig | any) => {
      if (
        comp.componentType === ComponentType.SELECT_DROPDOWN ||
        comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
        comp.componentType === ComponentType.MULTI_CHECKBOX
      ) {
        const dropdownComp = comp as any;
        // Handle both old format (LookupName) and new format (lookupName)
        const lookupName = dropdownComp.lookupName || dropdownComp.LookupName;
        if (lookupName) {
          lookupNames.push(lookupName);
        }
      }
      // Handle old format Fields property
      if ((comp as any).Fields) {
        (comp as any).Fields.forEach(processComponent);
      }
      if (comp.childComponents) {
        comp.childComponents.forEach(processComponent);
      }
    };
    config.forEach(processComponent);
    // Remove duplicates using Array.from
    return Array.from(new Set(lookupNames));
  };

  // Helper function to get lookup type from component type
  const getLookupType = (componentType: ComponentType): string => {
    if (componentType === ComponentType.SEARCHABLE_DROPDOWN) {
      return "Select";
    } else if (componentType === ComponentType.SELECT_DROPDOWN) {
      return "Select";
    } else if (componentType === ComponentType.MULTI_CHECKBOX) {
      return "Select"; // MultiCheckbox uses same lookup type as Select
    } else if (componentType === ComponentType.MULTI_SUB_LIST) {
      return "Select"; // MultiSubList uses Select lookup type
    }
    return "Select"; // Default
  };

  // Helper function to get all dependent lookup fields from config
  const getDependentLookups = (): Array<{
    fieldName: string;
    lookupName: string;
    parentField: string;
    componentType: ComponentType;
  }> => {
    const dependentLookups: Array<{
      fieldName: string;
      lookupName: string;
      parentField: string;
      componentType: ComponentType;
    }> = [];
    const processComponent = (comp: ComponentConfig | any) => {
      if (
        comp.componentType === ComponentType.SELECT_DROPDOWN ||
        comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
        comp.componentType === ComponentType.MULTI_CHECKBOX ||
        comp.componentType === ComponentType.SUB_MODULE
      ) {
        const dropdownComp = comp as any;
        // Handle both old format (LookupName) and new format (lookupName)
        const lookupName = dropdownComp.lookupName || dropdownComp.LookupName;
        // Handle both old format (BindParent) and new format (parentField)
        const parentField =
          dropdownComp.parentField ||
          (dropdownComp.BindParent
            ? Object.keys(dropdownComp.BindParent)[0]
            : undefined);
        if (lookupName && parentField) {
          dependentLookups.push({
            fieldName: comp.name || comp.BindValue,
            lookupName: lookupName,
            parentField: parentField,
            componentType: comp.componentType,
          });
        }
      }
      // Handle old format Fields property
      if ((comp as any).Fields) {
        (comp as any).Fields.forEach(processComponent);
      }
      if (comp.childComponents) {
        comp.childComponents.forEach(processComponent);
      }
      // Handle childControls in SUB_MODULE components
      if ((comp as any).childControls) {
        (comp as any).childControls.forEach(processComponent);
      }
    };
    config.forEach(processComponent);
    return dependentLookups;
  };

  // Get lookups state from Redux
  const lookupsState = useAppSelector((state) => state.lookups);

  // Purchase Order edit fallback: if Vendor ID is missing but vendor label exists, resolve ID from supplier lookup.
  useEffect(() => {
    if (String(screenId) !== "130" || !isEditMode || !onFormDataChange) return;

    const currentSupplierValue = formData?.drp_supplier;
    if (
      currentSupplierValue !== undefined &&
      currentSupplierValue !== null &&
      String(currentSupplierValue).trim() !== ""
    ) {
      return;
    }

    const selectedData = (formData?.drp_supplier_selectedData || {}) as any;
    const supplierOptions = (lookupsState.lookups?.supplier || []) as any[];

    const directSupplierId =
      selectedData?.id ??
      selectedData?.value ??
      formData?.supplier_id ??
      formData?.sup_id ??
      formData?.supplierid;

    if (
      directSupplierId !== undefined &&
      directSupplierId !== null &&
      String(directSupplierId).trim() !== ""
    ) {
      const nextId = String(directSupplierId);
      if (vendorResolutionRef.current !== `id:${nextId}`) {
        vendorResolutionRef.current = `id:${nextId}`;
        onFormDataChange("drp_supplier", nextId);
      }
      return;
    }

    const supplierLabelCandidate =
      selectedData?.label ??
      selectedData?.name ??
      formData?.supplier_id_desc ??
      formData?.supplier_name ??
      formData?.suppliername ??
      formData?.vendor_name ??
      formData?.vendor;

    if (!supplierLabelCandidate || supplierOptions.length === 0) return;

    const normalizedLabel = String(supplierLabelCandidate).trim().toLowerCase();
    if (!normalizedLabel) return;

    const matchedOption = supplierOptions.find((opt: any) => {
      const optLabel = String(opt?.label ?? opt?.name ?? "")
        .trim()
        .toLowerCase();
      return optLabel === normalizedLabel;
    });

    if (!matchedOption) return;

    const resolvedId = matchedOption?.value ?? matchedOption?.id;
    if (
      resolvedId === undefined ||
      resolvedId === null ||
      String(resolvedId).trim() === ""
    )
      return;

    const nextId = String(resolvedId);
    if (vendorResolutionRef.current === `label:${normalizedLabel}:${nextId}`) return;
    vendorResolutionRef.current = `label:${normalizedLabel}:${nextId}`;

    onFormDataChange("drp_supplier", nextId);
    onFormDataChange("drp_supplier_selectedData", {
      id: nextId,
      value: nextId,
      label: String(
        matchedOption?.label ?? matchedOption?.name ?? supplierLabelCandidate
      ),
      name: String(
        matchedOption?.name ?? matchedOption?.label ?? supplierLabelCandidate
      ),
      ...matchedOption,
    });

    if (import.meta.env.DEV) {
      console.log("[Vendor Prefill Debug][ResolveFromLabel]", {
        supplierLabelCandidate,
        resolvedId: nextId,
        matchedOption,
      });
    }
  }, [screenId, isEditMode, formData, lookupsState.lookups, onFormDataChange]);

  // Convert label default values to IDs when lookup options become available
  useEffect(() => {
    const convertLabelDefaultsToIds = () => {
      const processComponent = (comp: ComponentConfig | any) => {
        if (
          (comp.componentType === ComponentType.SELECT_DROPDOWN ||
            comp.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
          (comp.lookupName || comp.LookupName)
        ) {
          const fieldName = comp.name || comp.BindValue;
          const lookupName = comp.lookupName || comp.LookupName;
          const defaultValue = getDefaultValue(comp);

          if (fieldName && defaultValue && lookupName) {
            const currentValue = formData[fieldName];
            const lookupKey = lookupName;
            const lookupOptions = lookupsState.lookups[lookupKey] || [];

            // If current value matches the default value and default value looks like a label (contains %)
            // and options are available, try to convert it
            if (
              lookupOptions.length > 0 &&
              currentValue === defaultValue &&
              typeof defaultValue === "string" &&
              (defaultValue.includes("%") || defaultValue.length < 10) // Likely a label, not an ID
            ) {
              // Check if the value matches a label
              const matchingOption = lookupOptions.find(
                (opt: any) =>
                  opt.label === defaultValue ||
                  String(opt.label) === String(defaultValue) ||
                  (opt as any).name === defaultValue ||
                  String((opt as any).name) === String(defaultValue)
              );

              if (matchingOption) {
                // Convert label to ID
                const idValue =
                  matchingOption.value || (matchingOption as any).id || defaultValue;

                if (idValue !== defaultValue && onFormDataChange) {
                  onFormDataChange(fieldName, idValue);


                }
              }
            }
          }
        }

        // Process nested components
        if ((comp as any).Fields) {
          (comp as any).Fields.forEach(processComponent);
        }
        if (comp.childComponents) {
          comp.childComponents.forEach(processComponent);
        }
      };

      config.forEach(processComponent);
    };

    convertLabelDefaultsToIds();
  }, [config, formData, lookupsState.lookups, onFormDataChange]);

  // Fetch workflow options for workflow state fields when formData has values
  useEffect(() => {
    const fetchWorkflowOptionsForFieldsWithValues = async () => {
      const processComponent = (comp: ComponentConfig | any) => {
        if (comp.componentType === ComponentType.SELECT_DROPDOWN) {
          const lookupName = comp.lookupName || comp.LookupName;
          const fieldName = comp.name || comp.BindValue;
          const jsonAttr = comp.Json_Attribute;

          if (!lookupName || !fieldName) return;

          // Find form component to get Workflow property
          const findFormComponent = (comps: ComponentConfig[]): any => {
            for (const comp of comps) {
              if (comp.componentType === LayoutTypes.FORM) {
                return comp;
              }
              const compAny = comp as any;
              if (compAny.Fields) {
                const found = findFormComponent(compAny.Fields);
                if (found) return found;
              }
              if (comp.childComponents) {
                const found = findFormComponent(comp.childComponents);
                if (found) return found;
              }
            }
            return null;
          };
          const formComponent = findFormComponent(config);
          const workflowName = formComponent?.Workflow || formComponent?.workflow;

          // Check if this is a workflow state field
          if (workflowName) {
            // Check if field has a value in formData
            const fieldValue = formData[fieldName] || formData[jsonAttr];
            if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
              const workflowLookupKey = `workflow_${workflowName}_${lookupName}`;
              const workflowOptions = lookupsState.lookups[workflowLookupKey] || [];

              // If options not loaded and not already loading, fetch them
              if (workflowOptions.length === 0 &&
                !lookupsState.loading[workflowLookupKey] &&
                !requestedLookupsRef.current.has(workflowLookupKey)) {
                requestedLookupsRef.current.add(workflowLookupKey);



                // Fetch workflow and extract states
                (async () => {
                  try {
                    const workflow = await apiService.getWorkflow(workflowName);
                    if (workflow && workflow.States && Array.isArray(workflow.States)) {
                      // Find workflow state where Action === lookupName
                      const workflowState = workflow.States.find(
                        (state: any) => state.Action === lookupName
                      );
                      if (workflowState && workflowState.States && Array.isArray(workflowState.States)) {
                        // Convert States array to options
                        const workflowOptions = workflowState.States.map((state: string) => ({
                          id: state,
                          name: state,
                          value: state,
                          label: state,
                        }));
                        // Store in lookupsState using addLookupItem for each option
                        workflowOptions.forEach((option: any) => {
                          dispatch(
                            addLookupItem({
                              lookupKey: workflowLookupKey,
                              item: option,
                            })
                          );
                        });
                      }
                    }
                  } catch (error) {

                  } finally {
                    requestedLookupsRef.current.delete(workflowLookupKey);
                  }
                })();
              }
            }
          }
        }

        // Recursively process nested components
        if ((comp as any).Fields) {
          (comp as any).Fields.forEach(processComponent);
        }
        if (comp.childComponents) {
          comp.childComponents.forEach(processComponent);
        }
        if ((comp as any).childControls) {
          (comp as any).childControls.forEach(processComponent);
        }
        if (comp.componentType === LayoutTypes.FIELD_GROUP && comp.childComponents) {
          comp.childComponents.forEach(processComponent);
        }
      };

      config.forEach(processComponent);
    };

    fetchWorkflowOptionsForFieldsWithValues();
  }, [config, formData, lookupsState.lookups, lookupsState.loading, dispatch]);

  // Clear all cached lookups on screen mount so every screen always fetches fresh data
  useEffect(() => {
    dispatch(clearAllLookups());
    requestedLookupsRef.current.clear();
  }, [dispatch]);

  // Fetch independent lookups on mount and when config changes
  useEffect(() => {
    const fetchIndependentLookups = async () => {
      const independentLookups: Array<{
        lookupName: string;
        componentType: ComponentType;
        fieldName: string;
      }> = [];

      // Extract independent lookups
      const processComponent = (comp: ComponentConfig | any) => {
        if (
          comp.componentType === ComponentType.SELECT_DROPDOWN ||
          comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
          comp.componentType === ComponentType.MULTI_CHECKBOX ||
          comp.componentType === ComponentType.MULTI_SUB_LIST
        ) {
          const dropdownComp = comp as any;
          // Handle both old format (LookupName) and new format (lookupName)
          const lookupName = dropdownComp.lookupName || dropdownComp.LookupName;

          // Skip workflow state fields - they are handled separately
          // Find form component to check for Workflow property
          const findFormComponent = (comps: ComponentConfig[]): any => {
            for (const comp of comps) {
              if (comp.componentType === LayoutTypes.FORM) {
                return comp;
              }
              const compAny = comp as any;
              if (compAny.Fields) {
                const found = findFormComponent(compAny.Fields);
                if (found) return found;
              }
              if (comp.childComponents) {
                const found = findFormComponent(comp.childComponents);
                if (found) return found;
              }
            }
            return null;
          };
          const formComponent = findFormComponent(config);
          const workflowName = formComponent?.Workflow || formComponent?.workflow;

          // Skip only workflow state dropdowns (SELECT_DROPDOWN/SEARCHABLE_DROPDOWN in a workflow form).
          // MULTI_CHECKBOX and MULTI_SUB_LIST always need their lookups (e.g. work_request, inpection_items).
          const isWorkflowStateDropdown =
            workflowName &&
            lookupName &&
            (comp.componentType === ComponentType.SELECT_DROPDOWN ||
              comp.componentType === ComponentType.SEARCHABLE_DROPDOWN);
          if (isWorkflowStateDropdown) {
            return; // Skip workflow state fields - handled by workflow fetching logic
          }

          if (lookupName && !dropdownComp.parentField) {
            // Check if we already have this lookup with the same component type
            const existing = independentLookups.find(
              (l) =>
                l.lookupName === lookupName &&
                l.componentType === comp.componentType
            );
            if (!existing) {
              independentLookups.push({
                lookupName: lookupName,
                componentType: comp.componentType,
                fieldName: comp.name || comp.BindValue,
              });
            }
          }
        }

        // Handle old format Fields property
        if ((comp as any).Fields) {
          (comp as any).Fields.forEach(processComponent);
        }
        if (comp.childComponents) {
          comp.childComponents.forEach(processComponent);
        }
        // Handle childControls in SUB_MODULE components
        if ((comp as any).childControls) {
          (comp as any).childControls.forEach(processComponent);
        }
        // Handle FIELD_GROUP components - they also have childComponents
        if (comp.componentType === LayoutTypes.FIELD_GROUP && comp.childComponents) {
          comp.childComponents.forEach(processComponent);
        }
      };
      config.forEach(processComponent);

      // Fetch independent lookups
      for (const { lookupName, componentType } of independentLookups) {
        // Check if already loaded or loading in Redux
        const lookupKey = lookupName;
        const isLoading = lookupsState.loading[lookupKey] || false;
        const existingOptions = lookupsState.lookups[lookupKey] || [];

        // Log for get_all_ledgers, journal_ledgers, and Approve
        if (
          lookupName === "get_all_ledgers" ||
          lookupName === "journal_ledgers" ||
          lookupName === "Approve"
        ) {
          const lastFetched = lookupsState.lastFetched[lookupKey];

        }

        // Check if we already requested this lookup for this screen session
        if (requestedLookupsRef.current.has(lookupKey)) {
          continue;
        }

        // Mark as requested immediately to prevent duplicate fetches
        requestedLookupsRef.current.add(lookupKey);

        // Logic to skip if already loading/loaded was removed to force refetch on screen open
        // This ensures fresh data when navigating to a screen
        if (isLoading) {

          continue;
        }

        // Log before dispatching fetch for get_all_ledgers, journal_ledgers, and Approve
        if (
          lookupName === "get_all_ledgers" ||
          lookupName === "journal_ledgers" ||
          lookupName === "Approve"
        ) {

        }

        // Dispatch fetch lookup thunk
        dispatch(
          fetchLookup({
            lookupName,
            params: {
              parentid: "",
              lookuptype: getLookupType(componentType),
            },
          })
        );
      }
    };

    fetchIndependentLookups();
  }, [config, dispatch]);

  // Fetch lookups and rawData for fields that have values set (for ActionArgs scenario)
  // This ensures lookups are fetched when formData has values but options aren't loaded yet
  useEffect(() => {
    const fetchLookupsForFieldsWithValues = () => {
      const processComponent = (comp: ComponentConfig | any) => {
        if (
          comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
          comp.componentType === ComponentType.SELECT_DROPDOWN
        ) {
          const dropdownComp = comp as any;
          // Handle both old format (LookupName) and new format (lookupName)
          const lookupName = dropdownComp.lookupName || dropdownComp.LookupName;
          const fieldName = comp.name || comp.BindValue;
          const jsonAttr = comp.Json_Attribute;

          if (lookupName && fieldName && !dropdownComp.parentField) {
            // Check both field name and Json_Attribute for value
            let fieldValue = formData[fieldName];
            if ((fieldValue === undefined || fieldValue === null || fieldValue === "") && jsonAttr) {
              fieldValue = formData[jsonAttr];
            }

            // Determine correct lookup type based on component type
            const lookupType = comp.componentType === ComponentType.SELECT_DROPDOWN
              ? "Select"
              : "Select";

            // If field has a value, ensure lookup is fetched
            if (fieldValue && (fieldValue !== "" && fieldValue !== null && fieldValue !== undefined)) {
              const lookupKey = lookupName;
              const currentOptions = lookupsState.lookups[lookupKey] || [];
              const currentRawData = lookupsState.rawData[lookupKey] || [];

              // Fetch lookup options if not already loading
              if (
                !lookupsState.loading[lookupKey] &&
                !requestedLookupsRef.current.has(lookupKey)
              ) {
                requestedLookupsRef.current.add(lookupKey);

                dispatch(
                  fetchLookup({
                    lookupName: lookupName,
                    params: {
                      parentid: "",
                      lookuptype: lookupType,
                    },
                  })
                );
                // CRITICAL: Do NOT delete from requestedLookupsRef on success
                // to prevent infinite loops if the result is empty
              }

              // Also fetch rawData if not already loading (needed for Report component)
              if (
                !lookupsState.loading[lookupKey] &&
                !requestedLookupsRef.current.has(`${lookupKey}_rawData`)
              ) {
                requestedLookupsRef.current.add(`${lookupKey}_rawData`);
                dispatch(
                  fetchSubModuleRawData({
                    lookupName: lookupName,
                    parentValue: "",
                    lookuptype: lookupType,
                  })
                );
                // CRITICAL: Do NOT delete from requestedLookupsRef on success
              }
            }
          }
        }

        // Recursively process nested components
        if ((comp as any).Fields) {
          (comp as any).Fields.forEach(processComponent);
        }
        if (comp.childComponents) {
          comp.childComponents.forEach(processComponent);
        }
        if ((comp as any).childControls) {
          (comp as any).childControls.forEach(processComponent);
        }
        // Handle FIELD_GROUP components - they also have childComponents
        if (comp.componentType === LayoutTypes.FIELD_GROUP && comp.childComponents) {
          comp.childComponents.forEach(processComponent);
        }
      };

      config.forEach(processComponent);
    };

    fetchLookupsForFieldsWithValues();
  }, [config, formData, lookupsState.lookups, lookupsState.rawData, lookupsState.loading, dispatch]);

  // Fetch dependent lookups when parent values change
  useEffect(() => {
    // Skip fetching during default value initialization to prevent multiple requests
    if (isInitializingDefaultsRef.current) {
      return;
    }

    const fetchDependentLookups = async () => {
      const dependentLookups = getDependentLookups();

      // Build a string representation of current parent values to detect changes
      const currentParentValuesString = dependentLookups
        .map(({ parentField }) => {
          const value = formData[parentField];
          return `${parentField}:${value !== undefined && value !== null ? value : ""}`;
        })
        .sort()
        .join("|");

      // Only proceed if parent values have actually changed
      if (currentParentValuesString === previousParentValuesStringRef.current) {
        // Parent values haven't changed, but check if we need to fetch any missing lookups
        const hasUnfetchedLookups = dependentLookups.some(
          ({ lookupName, parentField, componentType }) => {
            const parentValue = formData[parentField];
            if (parentValue === undefined || parentValue === null || parentValue === "") {
              return false;
            }
            const lookupKey = `${lookupName}_${parentValue}`;
            const isLoading = lookupsState.loading[lookupKey] || false;

            if (componentType === ComponentType.SUB_MODULE) {
              const existingRawData = lookupsState.rawData[lookupKey] || [];
              return existingRawData.length === 0 && !isLoading && !requestedLookupsRef.current.has(lookupKey);
            }

            const existingOptions = lookupsState.lookups[lookupKey] || [];
            return existingOptions.length === 0 && !isLoading && !requestedLookupsRef.current.has(lookupKey);
          }
        );

        if (!hasUnfetchedLookups) {
          return;
        }
      } else {
        // Parent values changed - clear requested lookups to allow re-fetching
        requestedLookupsRef.current.clear();
        previousParentValuesStringRef.current = currentParentValuesString;
      }

      // Fetch dependent lookups for each field
      for (const { fieldName, lookupName, parentField, componentType } of dependentLookups) {
        const parentValue = formData[parentField];
        if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
          const lookupKey = `${lookupName}_${parentValue}`;
          const isLoading = lookupsState.loading[lookupKey] || false;
          const existingOptions = lookupsState.lookups[lookupKey] || [];

          if (requestedLookupsRef.current.has(lookupKey)) {
            continue;
          }

          // SPECIAL HANDLING FOR SUB_MODULE
          if (componentType === ComponentType.SUB_MODULE) {
            const existingRawData = lookupsState.rawData[lookupKey];
            if (existingRawData && existingRawData.length > 0) {
              onFormDataChange?.(fieldName, existingRawData);
              continue;
            }
            if (isLoading) {
              continue;
            }
          }

          if (existingOptions.length > 0 || isLoading) {
            continue;
          }

          requestedLookupsRef.current.add(lookupKey);

          if (componentType === ComponentType.SUB_MODULE) {
            dispatch(
              fetchSubModuleRawData({
                lookupName: lookupName,
                parentValue: parentValue,
                lookuptype: "Select",
              })
            ).then((result: any) => {
              // Update formData with the fetched results
              if (result.payload && result.payload.rawData && Array.isArray(result.payload.rawData)) {
                onFormDataChange?.(fieldName, result.payload.rawData);
              } else if (result.payload && Array.isArray(result.payload)) {
                onFormDataChange?.(fieldName, result.payload);
              } else {
                onFormDataChange?.(fieldName, []);
              }
            });
            // CRITICAL: requestedLookupsRef is NOT cleared on success metadata fetch
          } else {
            dispatch(
              fetchDependentLookup({
                lookupName,
                parentValue,
                lookuptype: getLookupType(componentType),
              })
            ).catch(() => {
              // Only remove on error to allow retries
              requestedLookupsRef.current.delete(lookupKey);
            });
          }
        }
      }
    };

    fetchDependentLookups();
  }, [config, formData, onFormDataChange, dispatch, isEditMode, lookupsState.loading, lookupsState.lastFetched, lookupsState.lookups, lookupsState.rawData]);

  // Initialize previous parent values when form finishes loading in edit mode
  useEffect(() => {
    if (isEditMode && !isLoading) {
      // Form has finished loading, initialize ref with current parent values
      // This happens after the full record is loaded
      const initializeRef = (comp: ComponentConfig | any) => {
        if (
          comp.componentType === ComponentType.TEXT_FIELD &&
          comp.BindParent
        ) {
          const parentField = Object.keys(comp.BindParent)[0];
          const currentParentValue = formData[parentField];
          if (currentParentValue !== undefined) {
            // Only set if not already set (to preserve on subsequent renders)
            if (previousParentValuesRef.current[parentField] === undefined) {
              previousParentValuesRef.current[parentField] = currentParentValue;
            }
          }
        }
        if ((comp as any).Fields) {
          (comp as any).Fields.forEach(initializeRef);
        }
        if (comp.childComponents) {
          comp.childComponents.forEach(initializeRef);
        }
      };
      config.forEach(initializeRef);

      // Mark initial load as complete after a brief delay to ensure all data is set
      const timeoutId = setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);

      return () => clearTimeout(timeoutId);
    } else if (!isEditMode) {
      // Clear refs when exiting edit mode
      previousParentValuesRef.current = {};
      isInitialLoadRef.current = true;
    } else if (isEditMode && isLoading) {
      // Reset initial load flag when loading starts
      isInitialLoadRef.current = true;
    }
  }, [isEditMode, isLoading, config, formData]);

  // Handle BindParent auto-population for text fields when parent dropdown changes
  // This is a fallback that runs when formData changes (including initial load from ActionArgs)
  useEffect(() => {
    const processComponent = (comp: ComponentConfig | any) => {
      // Check if this is a text field with BindParent
      if (comp.componentType === ComponentType.TEXT_FIELD && comp.BindParent) {
        const bindValue = comp.BindValue || comp.name;
        const parentField = Object.keys(comp.BindParent)[0];
        const parentFieldAttribute = comp.BindParent[parentField];
        const parentValue = formData[parentField];
        const previousParentValue =
          previousParentValuesRef.current[parentField];

        // Detect if parent value was changed by user (not initial load)
        // In edit mode, only consider it user-initiated if:
        // 1. We have a previous value (not initial load)
        // 2. The value actually changed
        // 3. We're not in the initial load phase
        const isUserInitiatedChange =
          !isInitialLoadRef.current &&
          previousParentValue !== undefined &&
          previousParentValue !== parentValue;

        if (
          parentValue !== undefined &&
          parentValue !== null &&
          parentValue !== ""
        ) {
          // Find the lookup for the parent field
          const findParentLookup = (
            comp: ComponentConfig | any
          ): string | null => {
            if (
              (comp.componentType === ComponentType.SELECT_DROPDOWN ||
                comp.componentType === ComponentType.SEARCHABLE_DROPDOWN) &&
              (comp.BindValue === parentField || comp.name === parentField)
            ) {
              return comp.LookupName || comp.lookupName || null;
            }
            if ((comp as any).Fields) {
              for (const field of (comp as any).Fields) {
                const lookup = findParentLookup(field);
                if (lookup) return lookup;
              }
            }
            if (comp.childComponents) {
              for (const child of comp.childComponents) {
                const lookup = findParentLookup(child);
                if (lookup) return lookup;
              }
            }
            // Handle childControls in SUB_MODULE components
            if ((comp as any).childControls) {
              for (const child of (comp as any).childControls) {
                const lookup = findParentLookup(child);
                if (lookup) return lookup;
              }
            }
            return null;
          };

          const parentLookupName = findParentLookup(config);
          if (parentLookupName) {
            // Priority 1: Check if full selected record is stored in formData (from ActionArgs or onChange)
            const selectedDataKey = `${parentField}_selectedData`;
            const selectedDataFromForm = formData[selectedDataKey];

            // Priority 2: Get lookup options from Redux state
            const lookupOptions = lookupsState.lookups[parentLookupName] || [];
            const rawData = lookupsState.rawData[parentLookupName] || [];

            // Try to find the selected option/record
            let selectedRecord: any = null;

            if (selectedDataFromForm && Object.keys(selectedDataFromForm).length > 0) {
              // Use the stored selected data (from ActionArgs or onChange)
              selectedRecord = selectedDataFromForm;
            } else {
              // Try to find in rawData first (has all properties)
              selectedRecord = rawData.find(
                (item: any) =>
                  item.id === parentValue ||
                  String(item.id) === String(parentValue) ||
                  item.value === parentValue ||
                  String(item.value) === String(parentValue)
              );

              // If not found in rawData, try lookup options
              if (!selectedRecord) {
                selectedRecord = lookupOptions.find(
                  (opt: any) =>
                    opt.value === parentValue ||
                    String(opt.value) === String(parentValue) ||
                    opt.id === parentValue ||
                    String(opt.id) === String(parentValue)
                );
              }
            }

            if (selectedRecord) {
              // Get the value from the selected record using the attribute name
              const recordData = selectedRecord as any;
              const attributeValue =
                recordData[parentFieldAttribute] ||
                recordData[parentFieldAttribute.toLowerCase()] ||
                recordData[parentFieldAttribute.toUpperCase()] ||
                "";

              const currentValue = formData[bindValue];

              // Auto-populate logic:
              // 1. In CREATE mode: always auto-populate when parent changes, lookups arrive, or on initial load
              // 2. In EDIT mode: only auto-populate if:
              //    - Field is empty/null, OR
              //    - User explicitly changed the parent field (not initial load)
              // 3. On initial load (from ActionArgs): always populate if selectedData is available
              const hasSelectedData = !!selectedDataFromForm;
              const shouldAutoPopulate =
                !isEditMode ||
                currentValue === undefined ||
                currentValue === null ||
                currentValue === "" ||
                isUserInitiatedChange ||
                (hasSelectedData && isInitialLoadRef.current);

              if (shouldAutoPopulate && currentValue !== attributeValue) {

                onFormDataChange?.(bindValue, attributeValue);
              }
            }
          }
        }

        // Update previous parent value tracking to prevent loops
        if (parentValue !== undefined && parentValue !== null) {
          previousParentValuesRef.current[parentField] = parentValue;
        }
      }

      // Process nested components recursively
      if ((comp as any).Fields) {
        (comp as any).Fields.forEach(processComponent);
      }
      if (comp.childComponents) {
        comp.childComponents.forEach(processComponent);
      }
      if ((comp as any).childControls) {
        (comp as any).childControls.forEach(processComponent);
      }
      if (comp.componentType === LayoutTypes.FIELD_GROUP && comp.childComponents) {
        comp.childComponents.forEach(processComponent);
      }
    };

    config.forEach(processComponent);
  }, [
    config,
    formData,
    onFormDataChange,
    lookupsState.lookups,
    lookupsState.rawData,
    isEditMode,
    isLoading,
  ]);

  // Handle BindParent for SEARCHABLE_DROPDOWN fields (like Customer name from jobcard)
  // This ensures dropdowns populate when parentid_selectedData is available
  useEffect(() => {
    const processDropdownBindParent = (comp: ComponentConfig | any) => {
      if (
        (comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
          comp.componentType === ComponentType.SELECT_DROPDOWN) &&
        comp.BindParent
      ) {
        const bindValue = comp.BindValue || comp.name;
        const parentField = Object.keys(comp.BindParent)[0];
        const bindColumn = comp.BindParent[parentField];
        const parentValue = formData[parentField];
        const selectedDataKey = `${parentField}_selectedData`;
        const parentSelectedData = formData[selectedDataKey];

        // Only populate if we have parent value and selected data (from jobcard/invoice)
        if (parentValue && parentSelectedData && bindColumn) {
          const currentValue = formData[bindValue];
          // Try to get value from parentSelectedData with fallbacks
          let attributeValue =
            parentSelectedData[bindColumn] ||
            parentSelectedData[bindColumn.toLowerCase()] ||
            parentSelectedData[bindColumn.toUpperCase()];
          if (attributeValue == null && bindColumn === "customertype") {
            attributeValue = parentSelectedData.customer_type ?? parentSelectedData.customer_type_id ?? "";
          }
          if (attributeValue == null && bindColumn === "insurance_name") {
            attributeValue =
              parentSelectedData.insurance_id ??
              parentSelectedData.customer_id ??
              parentSelectedData.customer_name ??
              "";
          }
          if (attributeValue == null && bindColumn === "invoice_date") {
            attributeValue = parentSelectedData.job_date ?? parentSelectedData.date ?? "";
          }

          // Auto-populate if value found and current value is empty or different
          if (
            attributeValue !== undefined &&
            attributeValue !== null &&
            attributeValue !== "" &&
            (currentValue === undefined || currentValue === null || currentValue === "" || currentValue !== String(attributeValue))
          ) {
            onFormDataChange?.(bindValue, String(attributeValue));
            // Ensure lookup is fetched for dropdown display
            if (comp.LookupName) {
              dispatch(fetchLookup({ lookupName: comp.LookupName, params: {} }));
            }
          }
        }
      }

      // Process nested components
      if ((comp as any).Fields) {
        (comp as any).Fields.forEach(processDropdownBindParent);
      }
      if (comp.childComponents) {
        comp.childComponents.forEach(processDropdownBindParent);
      }
      if ((comp as any).childControls) {
        (comp as any).childControls.forEach(processDropdownBindParent);
      }
      if (comp.componentType === LayoutTypes.FIELD_GROUP && comp.childComponents) {
        comp.childComponents.forEach(processDropdownBindParent);
      }
    };

    config.forEach(processDropdownBindParent);
  }, [
    config,
    formData,
    onFormDataChange,
    dispatch,
    isEditMode,
    isLoading,
  ]);

  // Update previous parent values after processing (but only after initial load)
  useEffect(() => {
    if (!isEditMode || !isInitialLoadRef.current) {
      const updateRef = (comp: ComponentConfig | any) => {
        if (
          comp.componentType === ComponentType.TEXT_FIELD &&
          comp.BindParent
        ) {
          const parentField = Object.keys(comp.BindParent)[0];
          const currentParentValue = formData[parentField];
          if (currentParentValue !== undefined) {
            previousParentValuesRef.current[parentField] = currentParentValue;
          }
        }
        if ((comp as any).Fields) {
          (comp as any).Fields.forEach(updateRef);
        }
        if (comp.childComponents) {
          comp.childComponents.forEach(updateRef);
        }
      };
      config.forEach(updateRef);
    }
  }, [config, formData, isEditMode]);

  // Track SubModule footer values to detect when they're available
  const subModuleFooterKeys = useMemo(() => {
    return [
      'spare_amount', 'spare_vat_amount', 'spare_net_amount',
      'labour_amount', 'labour_vat_amount', 'labour_net_amount',
      'miscellaneous_amount', 'miscellanious_vat_amount', 'miscellaneous_net_amount',
    ];
  }, []);

  const hasSubModuleFooterData = useMemo(() => {
    // Check if any footer key exists in formData (including 0 as valid)
    return subModuleFooterKeys.some(key =>
      formData[key] !== undefined && formData[key] !== null
    );
  }, [formData, subModuleFooterKeys]);

  // Track SubModule footer values to trigger recalculation when they change
  // This ensures main form totals recalculate when SubModule footer values update
  const subModuleFooterValues = useMemo(() => {
    return subModuleFooterKeys.map(key => formData[key]).join('|');
  }, [formData, subModuleFooterKeys]);

  // Recalculate ExpressionV2 fields when formData changes (for cross-sub-module totals)
  useEffect(() => {
    // In edit mode, wait for loading to complete before recalculating
    // But don't skip if footer data is available (it might have been set)
    if (isEditMode && isLoading) {

      return;
    }

    // If in edit mode and no footer data yet, skip (will be handled by the footer-specific useEffect)
    // But if footer data exists, proceed with recalculation
    if (isEditMode && !hasSubModuleFooterData) {

      return;
    }

    // Find all TEXT_FIELD components with ExpressionV2
    const findExpressionFields = (comps: ComponentConfig[]): any[] => {
      const fields: any[] = [];
      const walk = (components: ComponentConfig[]) => {
        components.forEach((comp: any) => {
          if (comp.componentType === ComponentType.TEXT_FIELD && comp.ExpressionV2) {
            fields.push(comp);
          }
          if (comp.Fields && Array.isArray(comp.Fields)) {
            walk(comp.Fields);
          }
          if (comp.childComponents && Array.isArray(comp.childComponents)) {
            walk(comp.childComponents);
          }
        });
      };
      walk(comps);
      return fields;
    };

    const expressionFields = findExpressionFields(config);



    // Recalculate each expression field
    expressionFields.forEach((fieldConfig: any) => {
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      const expressionV2 = fieldConfig.ExpressionV2;

      if (!fieldName || !expressionV2) return;

      try {
        // Helper function to resolve cross-sub-module references
        const resolveCrossSubModuleReference = (match: string): string => {
          const crossRefMatch = match.match(/\[\(([^)]+)\)\.([^\]]+)\]/);
          if (crossRefMatch) {
            const subModuleName = crossRefMatch[1];
            const fieldBindValue = crossRefMatch[2];

            const findSubModuleConfig = (comps: ComponentConfig[]): any => {
              for (const comp of comps) {
                const compAny = comp as any;
                if (comp.componentType === ComponentType.SUB_MODULE) {
                  // Check multiple properties: name, Name, TableName
                  if (comp.name === subModuleName ||
                    compAny.Name === subModuleName ||
                    compAny.TableName === subModuleName) {
                    return comp;
                  }
                }
                // CRITICAL: Check Fields property (used by FORM components)
                if (compAny.Fields && Array.isArray(compAny.Fields)) {
                  const found = findSubModuleConfig(compAny.Fields);
                  if (found) return found;
                }
                // Check childComponents (used by GROUP, GRID, etc.)
                if (comp.childComponents && Array.isArray(comp.childComponents)) {
                  const found = findSubModuleConfig(comp.childComponents);
                  if (found) return found;
                }
                // Check childControls (used by SubModule itself)
                if (compAny.childControls && Array.isArray(compAny.childControls)) {
                  const found = findSubModuleConfig(compAny.childControls);
                  if (found) return found;
                }
              }
              return null;
            };

            const subModuleConfig = findSubModuleConfig(config);

            if (subModuleConfig) {
              const footerControls = (subModuleConfig as any).FooterControls || [];


              const footerField = footerControls.find((fc: any) =>
                (fc.name || fc.BindValue) === fieldBindValue
              );

              if (footerField) {
                const jsonAttribute = footerField.Json_Attribute || footerField.json_Attribute || fieldBindValue;



                const value = formData[jsonAttribute];

                // Treat 0 as a valid value (not empty)
                if (value !== undefined && value !== null && value !== "") {
                  const numValue = typeof value === "number"
                    ? value
                    : parseFloat(String(value));



                  return String(isNaN(numValue) ? 0 : numValue);
                }
                // Also check if value is explicitly 0
                if (value === 0 || value === "0") {

                  return "0";
                }
              }
            }

            // Fallback: try multiple variations to find the value dynamically
            // DYNAMIC: Extract Json_Attribute from FooterControls if SubModule config exists
            const possibleKeys: string[] = [];

            // First, try to get Json_Attribute from FooterControls (most reliable)
            if (subModuleConfig) {
              const allFooterControls = (subModuleConfig as any).FooterControls || [];
              // Find all Json_Attributes from FooterControls that might match
              allFooterControls.forEach((fc: any) => {
                const fcBindValue = fc.name || fc.BindValue;
                const fcJsonAttribute = fc.Json_Attribute || fc.json_Attribute;

                // If BindValue matches, use its Json_Attribute
                if (fcBindValue === fieldBindValue && fcJsonAttribute) {
                  possibleKeys.push(fcJsonAttribute);
                }
              });
            }

            // Then try common patterns dynamically (without hardcoding submodule names)
            possibleKeys.push(
              fieldBindValue, // Try BindValue directly
              `${subModuleName}_${fieldBindValue}`, // Try submodule name prefix pattern
              fieldBindValue.replace(/^(txt_|drp_|dt_)/, ""), // Try removing common prefixes
              `${subModuleName}_${fieldBindValue.replace(/^(txt_|drp_|dt_)/, "")}`, // Try with submodule name and cleaned field name
            );

            // Remove duplicates
            const uniqueKeys = [...new Set(possibleKeys.filter(Boolean))] as string[];



            for (const key of uniqueKeys) {
              const fallbackValue = formData[key];
              // Check if value exists (including 0 as valid)
              if (fallbackValue !== undefined && fallbackValue !== null) {
                // Handle empty string separately
                if (fallbackValue === "") {

                  continue; // Skip empty strings, try next key
                }
                // Value exists (including 0)
                const numValue = typeof fallbackValue === "number"
                  ? fallbackValue
                  : parseFloat(String(fallbackValue));



                return String(isNaN(numValue) ? 0 : numValue);
              }
            }



            return "0";
          }
          return match;
        };

        let expression = expressionV2;

        // Replace cross-sub-module references
        // Use a map to track resolved values to avoid re-resolving the same reference
        const resolvedCrossRefs = new Map<string, string>();
        const crossRefRegex = /\[\([^)]+\)\.[^\]]+\]/g;
        // Use match() instead of exec() to avoid regex state issues
        const crossRefMatches = expression.match(crossRefRegex) || [];
        const uniqueMatches = [...new Set(crossRefMatches)] as string[]; // Remove duplicates
        uniqueMatches.forEach((matchStr: string) => {
          if (!resolvedCrossRefs.has(matchStr)) {
            resolvedCrossRefs.set(matchStr, resolveCrossSubModuleReference(matchStr));
          }
        });
        // Replace all occurrences (use global regex since replaceAll isn't available)
        resolvedCrossRefs.forEach((resolved, matchStr) => {
          // Escape special regex characters in matchStr
          const escapedMatch = matchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(escapedMatch, 'g');
          expression = expression.replace(regex, resolved);
        });

        // Replace regular field references (support time strings HH:mm for duration expressions)
        const fieldRefRegex = /\[(\w+)\]/g;
        const fieldMatches = expression.match(fieldRefRegex) || [];
        for (const match of fieldMatches) {
          const fieldRef = match.replace(/[\[\]]/g, "");
          const refValue = formData[fieldRef];
          const timeHours = timeStringToDecimalHours(refValue);
          const numVal =
            timeHours !== null
              ? timeHours
              : refValue !== undefined && refValue !== null && refValue !== ""
                ? parseFloat(String(refValue)) || 0
                : 0;
          expression = expression.replace(match, String(numVal));
        }

        // Replace lookup property references
        const lookupPropRegex = /\[(\w+)\.(\w+)\]/g;
        const lookupMatches = expression.match(lookupPropRegex) || [];
        for (const match of lookupMatches) {
          const lookupMatch = match.match(/\[(\w+)\.(\w+)\]/);
          if (lookupMatch) {
            const lookupFieldName = lookupMatch[1];
            const property = lookupMatch[2];
            const selectedDataKey = `${lookupFieldName}_selectedData`;
            const selectedData = formData[selectedDataKey];

            if (selectedData && selectedData[property] !== undefined) {
              expression = expression.replace(match, String(parseFloat(String(selectedData[property])) || 0));
            } else {
              expression = expression.replace(match, "0");
            }
          }
        }

        // Evaluate expression
        try {
          const evaluatedValue = Function(`"use strict"; return (${expression})`)();



          if (evaluatedValue !== null && evaluatedValue !== undefined && !isNaN(evaluatedValue)) {
            // Format to 2 decimal places for currency/numeric fields
            const calculatedValue = typeof evaluatedValue === "number"
              ? parseFloat(evaluatedValue.toFixed(2)).toFixed(2)
              : String(evaluatedValue);
            const currentValue = formData[fieldName];

            // Only update if value changed
            if (currentValue !== calculatedValue) {
              const updateKey = `${fieldName}_${expressionV2}`;
              const previousValue = previousCalculatedValuesRef.current.get(updateKey);

              if (previousValue !== calculatedValue) {
                previousCalculatedValuesRef.current.set(updateKey, calculatedValue);
                onFormDataChange?.(fieldName, calculatedValue);


              }
            }
          }
        } catch (evalError) {
          if (import.meta.env.DEV) {
            console.error(`[ExpressionV2] useEffect evaluation error for ${fieldName}:`, evalError, {
              expression: expressionV2,
              resolvedExpression: expression,
            });
          }
        }
      } catch (error) {

      }
    });
  }, [config, formData, onFormDataChange, isEditMode, isLoading, hasSubModuleFooterData, subModuleFooterKeys, subModuleFooterValues]);

  // Form Errors State
  const [formErrors, setFormErrors] = React.useState<Record<string, string>>({});
  // Pending file uploads (uploaded on form submit, not on Upload button)
  const [pendingFilesByField, setPendingFilesByField] = React.useState<Record<string, File>>({});

  // Track when footer values first appear (edit mode initialization)
  const footerValuesInitializedRef = useRef(false);
  useEffect(() => {
    if (hasSubModuleFooterData && !footerValuesInitializedRef.current) {
      footerValuesInitializedRef.current = true;

    }
  }, [hasSubModuleFooterData, formData, subModuleFooterKeys]);

  // Redundant hook removed to prevent infinite loops (consolidated into main ExpressionV2 useEffect)

  const handleFormDataChange = (name: string, value: any) => {
    // 0. Clear validation error if exists
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // 1. Notify parent of the primary change
    onFormDataChange?.(name, value);

    // 2. Handle BindParent auto-population for FORM fields
    // This allows fields to be automatically filled based on a selection in a parent dropdown
    // Find the config for the field that changed to see if it's a dropdown
    const findComponent = (comps: ComponentConfig[]): any => {
      for (const comp of comps) {
        const c = comp as any;
        // Check both name and BindValue
        if (c.name === name || c.BindValue === name) return c;

        // Recursively search in all possible child containers
        if (c.Fields && Array.isArray(c.Fields)) {
          const found = findComponent(c.Fields);
          if (found) return found;
        }
        if (c.childComponents && Array.isArray(c.childComponents)) {
          const found = findComponent(c.childComponents);
          if (found) return found;
        }
        if (c.childControls && Array.isArray(c.childControls)) {
          const found = findComponent(c.childControls);
          if (found) return found;
        }
      }
      return null;
    };

    const changedFieldConfig = findComponent(config);
    if (changedFieldConfig) {
      const lookupName = changedFieldConfig.lookupName || changedFieldConfig.LookupName;
      if (lookupName && value !== undefined && value !== null && value !== "") {
        // Determine the lookup key (handle dependent lookups)
        const parentFieldForLookup = changedFieldConfig.parentField || changedFieldConfig.Parent_Field || changedFieldConfig.ParentField;
        let lookupKey = lookupName;

        if (parentFieldForLookup) {
          const parentValue = formData[parentFieldForLookup];
          if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
            lookupKey = `${lookupName}_${parentValue}`;
          }
        }

        // Find selected data in lookups or rawData
        // rawData is preferred as it contains non-transformed API response
        const options = lookupsState.lookups[lookupKey] || [];
        const rawData = lookupsState.rawData[lookupKey] || [];

        // Try finding in rawData first
        let selectedItem = rawData.find((item: any) =>
          String(item.id || item.value || item.codeid || item.product_id) === String(value) ||
          String(item.name || item.label) === String(value)
        );

        // Fallback to transformed lookup options
        if (!selectedItem) {
          selectedItem = options.find((opt: any) =>
            String(opt.value) === String(value) || String(opt.id) === String(value)
          );
        }

        if (selectedItem) {


          // Store selected data for ExpressionV2 and other uses
          onFormDataChange?.(`${name}_selectedData`, selectedItem);

          // Find dependent fields and update them
          const updateDependentFields = (comps: ComponentConfig[]) => {
            comps.forEach((comp: any) => {
              const bindParent = comp.BindParent || comp.bindParent;
              if (bindParent && (bindParent[name] || bindParent[changedFieldConfig.name] || bindParent[changedFieldConfig.BindValue])) {
                // Determine the parent field key used in BindParent config
                const parentKey = bindParent[name] ? name : (bindParent[changedFieldConfig.name] ? changedFieldConfig.name : changedFieldConfig.BindValue);
                const attributeName = bindParent[parentKey];

                // Try multiple property name variations to find the value in selectedItem
                const attributeValue =
                  selectedItem[attributeName] ||
                  selectedItem[attributeName.toLowerCase()] ||
                  selectedItem[attributeName.toUpperCase()] ||
                  selectedItem[attributeName.replace(/_/g, "")] ||
                  selectedItem[attributeName.replace(/_/g, "").toLowerCase()] ||
                  selectedItem[attributeName.replace(/_([a-z])/g, (g: string) => g[1].toUpperCase())]; // camelCase

                if (attributeValue !== undefined && attributeValue !== null) {
                  const dependentFieldName = comp.name || comp.BindValue;
                  if (dependentFieldName) {

                    onFormDataChange?.(dependentFieldName, attributeValue);

                    // Also update Json_Attribute if present to ensure consistency
                    if (comp.Json_Attribute && comp.Json_Attribute !== dependentFieldName) {
                      onFormDataChange?.(comp.Json_Attribute, attributeValue);
                    }
                  }
                }
              }

              // Recursively check all child containers
              if (comp.Fields && Array.isArray(comp.Fields)) updateDependentFields(comp.Fields);
              if (comp.childComponents && Array.isArray(comp.childComponents)) updateDependentFields(comp.childComponents);
              if (comp.childControls && Array.isArray(comp.childControls)) updateDependentFields(comp.childControls);
            });
          };
          updateDependentFields(config);
        }
      }
    }
  };

  const handlePendingFile = (fieldName: string, file: File | null) => {
    setPendingFilesByField((prev) => {
      const next = { ...prev };
      if (file === null) delete next[fieldName];
      else next[fieldName] = file;
      return next;
    });
  };

  const handleActionTrigger = (actionId: string, data?: any) => {
    // Intercept Lock/Unlock actions
    if (actionId === "lock" || actionId === "unlock") {
      handleLockToggle();
      return;
    }

    let finalActionId = actionId;
    // If navigation action is generically "Navigate" but screenId is provided in config
    if (finalActionId && finalActionId.toLowerCase() === "navigate" && data?.buttonConfig?.screenId) {
      finalActionId = `navigate_${data.buttonConfig.screenId}`;
    }

    const payload = finalActionId === "save" ? { ...data, pendingFiles: pendingFilesByField } : data;
    // For navigation actions, include current form data so navigation can access it
    if (finalActionId.startsWith("navigate_") && formData) {
      payload.formData = formData;
    }
    onActionTrigger?.(finalActionId, payload);
  };

  // Helper function to check if component tree contains SUMMARY_LIST
  const hasSummaryList = (component: ComponentConfig): boolean => {
    if (component.componentType === ComponentType.SUMMARY_LIST) {
      return true;
    }
    if (component.childComponents) {
      return component.childComponents.some((child) => hasSummaryList(child));
    }
    return false;
  };

  // Helper function to get fabAction from SCREEN_GROUP
  const getFabAction = (component: ComponentConfig): FabAction | null => {
    if (component.componentType === LayoutTypes.SCREEN_GROUP) {
      const screenGroup = component as ScreenGroupComponent;
      return screenGroup.fabAction || null;
    }
    return null;
  };

  // Helper function to render FAB button
  const getFabIcon = (type: FabAction["type"]): React.ReactNode => {
    const iconSize = 24;
    switch (type) {
      case "add":
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        );
      case "edit":
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M16.862 4.487a2.25 2.25 0 1 1 3.182 3.182L8.735 18.978a4.5 4.5 0 0 1-1.897 1.126l-3.393.97a.75.75 0 0 1-.926-.926l.97-3.393a4.5 4.5 0 0 1 1.126-1.897L16.862 4.487z" />
            <path d="M19.5 7.125 16.875 4.5" />
          </svg>
        );
      case "delete":
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9.75 4.5h4.5a.75.75 0 0 1 .75.75V6h3.75" />
            <path d="M6.75 6h10.5" />
            <path d="m10.5 10.5.75 7.5" />
            <path d="m13.5 10.5-.75 7.5" />
            <path d="M5.25 6 6 19.5a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5L18.75 6" />
          </svg>
        );
      case "custom":
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        );
      default:
        return (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        );
    }
  };

  const renderFabButton = (fabAction: FabAction) => {
    // Role-based visibility for FAB (screen JSON can set fabAction.visibility; support typo "visiblity")
    const fabVisibility = ((fabAction as any).visibility ?? (fabAction as any).visiblity) as number[] | undefined;
    const fabLegacyVisibleForRoles = (fabAction as any).visibleForRoles as number[] | undefined;
    if (!isComponentVisible(fabVisibility, fabLegacyVisibleForRoles)) {
      return null;
    }

    const handleFabClick = () => {
      if (fabAction.screenId) {
        handleActionTrigger(`navigate_${fabAction.screenId}`, {
          screenId: fabAction.screenId,
          buttonConfig: {
            popup: Boolean((fabAction as any).popup),
            popupTitle: (fabAction as any).popupTitle || fabAction.tooltip,
            Label: fabAction.tooltip,
          },
        });
      } else if (fabAction.action) {
        handleActionTrigger(fabAction.action);
      }
    };

    const getLabel = () => {
      if (fabAction.tooltip) return fabAction.tooltip;
      switch (fabAction.type) {
        case "add": return "Add New";
        case "edit": return "Edit";
        case "delete": return "Delete";
        default: return "Action";
      }
    };

    const label = getLabel();

    return (
      <button
        onClick={handleFabClick}
        data-fab-button="true"
        data-fab-type={fabAction.type}
        data-tour="add-button"
        className="fixed bottom-6 right-6 w-12 h-12 bg-primary-500 text-white border-none rounded-full cursor-pointer flex items-center justify-center shadow-lg transition-all duration-200 hover:bg-primary-600 hover:shadow-xl hover:scale-110 active:scale-105 z-50"
        aria-label={label}
        title={label}
      >
        {getFabIcon(fabAction.type)}
      </button>
    );
  };

  // Helper function to parse columnNumber and return responsive grid CSS variables
  const getResponsiveGridVars = (
    columnNumber: string | undefined
  ): React.CSSProperties => {
    const defaultColumns = "m1 t2 w3";
    const cols = columnNumber || defaultColumns;
    const [mobile, tablet, web] = cols
      .split(" ")
      .map((col: string) => parseInt(col.substring(1)));

    return {
      "--mobile-cols": mobile,
      "--tablet-cols": tablet,
      "--desktop-cols": web,
    } as React.CSSProperties;
  };

  const getLayoutStyles = (component: ComponentConfig) => {
    // Determine default padding based on component type
    // FORM/OneAppAddEdit: 10px
    // GROUP: 16px (for proper spacing around tables and content)
    // Others: 0px
    const defaultPadding =
      (component.componentType === LayoutTypes.FORM || (component as any).componentType === "OneAppAddEdit")
        ? 10
        : (component.componentType === LayoutTypes.GROUP)
          ? 16
          : 0;

    // For SCREEN_GROUP, GROUP, and GRID/FORM/FIELD_GROUP, don't set backgroundColor to avoid inline styles
    // These will use CSS classes which support dark mode
    const isScreenGroup = component.componentType === LayoutTypes.SCREEN_GROUP;
    const isGroup = component.componentType === LayoutTypes.GROUP;
    const isGridContainer =
      component.componentType === LayoutTypes.GRID ||
      component.componentType === LayoutTypes.FORM ||
      (component as any).componentType === "OneAppAddEdit" ||
      component.componentType === LayoutTypes.FIELD_GROUP;

    // Check if this is a GROUP with flex-col, w-full, h-auto, rounded-lg classes
    const isGroupWithSpecificClasses = isGroup &&
      component.style?.direction !== FlexDirection.ROW &&
      component.style?.widthMode !== WidthMode.WRAP &&
      (component.style?.heightMode === HeightMode.WRAP || !component.style?.heightMode) &&
      component.style?.borderRadius;

    const paddingVal =
      component.style?.padding !== undefined
        ? component.style.padding
        : defaultPadding;

    const baseStyles: React.CSSProperties = {
      margin: component.style?.margin || 0,
      paddingTop: paddingVal,
      paddingRight: paddingVal,
      paddingBottom: paddingVal,
      paddingLeft: paddingVal,
      // Only set backgroundColor if it's explicitly provided and not SCREEN_GROUP, GROUP, or GRID containers
      // These components use CSS classes which handle dark mode automatically
      ...(isScreenGroup || isGroup || isGridContainer
        ? {}
        : { backgroundColor: component.style?.backgroundColor || "transparent" }),
      borderRadius: component.style?.borderRadius || undefined,
      overflow: component.style?.overflow,
      overflowY: component.style?.overflowY as any,
      overflowX: component.style?.overflowX as any,
      height: component.style?.height,
      ...(component.style?.justifyContent && { justifyContent: component.style.justifyContent as React.CSSProperties["justifyContent"] }),
      ...(component.style?.alignItems && { alignItems: component.style.alignItems as React.CSSProperties["alignItems"] }),
      // maxHeight: isGroupWithSpecificClasses
      //   ? "calc(-170px + 100vh)"
      //   : component.style?.maxHeight,
    };

    // Add CSS variables for responsive grid
    if (isGridContainer) {
      const gridComponent = component as any;
      return {
        ...baseStyles,
        ...getResponsiveGridVars(gridComponent.columnNumber),
      };
    }

    return baseStyles;
  };

  // Helper function to check if a component is a submit button
  const isSubmitButton = (component: ComponentConfig): boolean => {
    if (component.componentType === ComponentType.BUTTON) {
      const buttonComp = component as any;
      return (
        buttonComp.onPressAction === "save" ||
        (buttonComp.name &&
          (buttonComp.name.toLowerCase().includes("submit") ||
            buttonComp.name.toLowerCase().includes("save")))
      );
    }
    return false;
  };

  // Helper function to check if a component tree contains submit buttons
  const containsSubmitButton = (component: ComponentConfig): boolean => {
    if (isSubmitButton(component)) {
      return true;
    }
    if (component.childComponents) {
      return component.childComponents.some((child) =>
        containsSubmitButton(child)
      );
    }
    // Check Fields property for old format
    const compAny = component as any;
    if (compAny.Fields && Array.isArray(compAny.Fields)) {
      return compAny.Fields.some((field: any) => containsSubmitButton(field));
    }
    return false;
  };

  const getLayoutClasses = (component: ComponentConfig): string => {
    if (component.componentType === LayoutTypes.SCREEN_GROUP) {
      return "min-h-screens w-full "; // Design system: p-4 (16px)
    }

    if (component.componentType === LayoutTypes.GROUP) {
      const flexDirection =
        component.style?.direction === FlexDirection.ROW
          ? "flex-row"
          : "flex-col";
      const widthClass =
        component.style?.widthMode === WidthMode.WRAP ? "w-auto" : "w-full";
      const heightClass =
        component.style?.heightMode === HeightMode.WRAP
          ? "h-auto"
          : component.style?.heightMode === HeightMode.FULL
            ? "h-full"
            : "h-auto";
      const borderRadius = component.style?.borderRadius || 0;
      const borderWidth = component.style?.borderWidth || 0;

      // Add justify-center for ROW direction groups containing submit buttons
      const hasSubmitButton = containsSubmitButton(component);
      const justifyClass =
        flexDirection === "flex-row" && hasSubmitButton ? "justify-center" : "";

      // Add group-container class for dark mode support
      const gridContainerClass =
        // In popup mode, avoid adding the bordered `gridcontainer` styling to the submit button row.
        // It creates an extra boxed border around the sticky submit button area (e.g. Leave Approval popup).
        renderMode === "popup" && hasSubmitButton ? "" : "gridcontainer";

      return `flex ${flexDirection} ${widthClass} ${heightClass} gap-0 ${justifyClass} ${borderRadius ? "rounded-lg" : ""
        } ${borderWidth ? "border" : ""} group-container ${gridContainerClass}`;
    }

    if (
      component.componentType === LayoutTypes.GRID ||
      component.componentType === LayoutTypes.FORM ||
      (component as any).componentType === "OneAppAddEdit" ||
      component.componentType === LayoutTypes.FIELD_GROUP
    ) {
      // For GRID components, we'll handle centering at the item level
      return "grid gap-3 responsive-grid gridcontainer";
    }

    return "";
  };

  // Helper function to convert old format Fields to new format childComponents
  const convertFieldsToChildComponents = (fields: any[]): ComponentConfig[] => {
    return fields.map((field) => {
      // Handle IncludeLabel - only set label if IncludeLabel is true or undefined
      const shouldIncludeLabel = field.IncludeLabel !== false;
      const label = shouldIncludeLabel ? field.Label || field.label : undefined;

      const converted: any = {
        componentType: field.componentType,
        name: field.BindValue || field.name,
        label: label,
        placeholder: field.PlaceHolder || field.placeholder,
        required:
          field.IsMandatory !== undefined
            ? field.IsMandatory
            : field.required || false,
        disabled: field.IsReadOnly || field.disabled,
        inputType: field.inputType,
        lookupName: field.LookupName || field.lookupName,
        showInSummary: field.showInSummary,
        IncludeLabel: field.IncludeLabel,
        Json_Attribute: field.Json_Attribute,
        TableName: field.TableName,
        Name: field.Name,
        BindValue: field.BindValue,
        BindParent: field.BindParent,
        BindScreen: field.BindScreen,
        InputValue: field.InputValue,
        AutoPopulate: field.AutoPopulate,
        AutoPopulate_format: field.AutoPopulate_format,
        IsVerticalView: field.IsVerticalView,
        childComponents: field.childComponents,
        childControls: field.childControls,
        LookupName: field.LookupName,
        // Preserve all other properties
        ...Object.keys(field).reduce((acc, key) => {
          if (
            ![
              "name",
              "BindValue",
              "Label",
              "PlaceHolder",
              "IsMandatory",
              "IsReadOnly",
            ].includes(key)
          ) {
            acc[key] = field[key];
          }
          return acc;
        }, {} as any),
      };

      // Recursively convert nested childComponents if they exist
      if (field.childComponents) {
        converted.childComponents = convertFieldsToChildComponents(
          field.childComponents
        );
      }

      return converted;
    });
  };

  const renderComponent = (
    originalComponent: ComponentConfig,
    index: number,
    parentFormConfig?: FormComponent | null
  ): React.ReactNode => {
    // Clone component to avoid mutating original config and to apply lock overrides
    let component = { ...originalComponent };
    const compName = (component as any).name || (component as any).BindValue || (component as any).Name || index;
    const key = `${component.componentType}-${compName}`;

    // Hide component based on platform visibility (componentVisiblity)
    if (!isComponentVisibleOnPlatform(component)) {
      return null;
    }

    // Hide component based on role visibility (visibility / visibleForRoles)
    // Screen JSON commonly uses `visibility: [roleIds...]` to control access.
    const roleVisibility = (component as any).visibility;
    const legacyVisibleForRoles = (component as any).visibleForRoles;
    if (!isComponentVisible(roleVisibility, legacyVisibleForRoles)) {
      return null;
    }

    // Hide component based on data state
    if (component.HideOnStates && Array.isArray(component.HideOnStates)) {
      const currentState = formData?.current_state || formData?.state;
      if (currentState && component.HideOnStates.includes(currentState)) {
        console.log(`[ComponentRenderer] Hiding component ${compName} due to HideOnStates matching current_state: ${currentState}`);
        return null;
      }
    }


    // Apply Screen Lock Logic
    // If screen is locked, disable all input components
    if (isScreenLocked) {
      const isInput =
        component.componentType === ComponentType.TEXT_FIELD ||
        component.componentType === ComponentType.TEXT_AREA ||
        component.componentType === ComponentType.RADIO_FIELD ||
        component.componentType === ComponentType.DATE_FIELD ||
        component.componentType === ComponentType.DATETIME_FIELD ||
        component.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
        component.componentType === ComponentType.SELECT_DROPDOWN ||
        component.componentType === ComponentType.IMAGE_UPLOAD ||
        component.componentType === ComponentType.FILES ||
        component.componentType === ComponentType.CHECKBOX ||
        component.componentType === ComponentType.MULTI_CHECKBOX ||
        component.componentType === ComponentType.SUB_MODULE ||
        component.componentType === ComponentType.MULTI_SUB_LIST;

      if (isInput) {
        (component as any).disabled = true;
        (component as any).IsReadOnly = true; // Use both for maximum compatibility
        // For SubModule/Grid, we might need to rely on the component handling 'disabled' prop
      }

      // Also disable Submit/Save buttons when locked
      if (isSubmitButton(component)) {
        (component as any).disabled = true;
      }
    }

    // Update Lock Button State
    // If this is the lock button, update its action and visual state based on isScreenLocked
    if (
      (component as any).name === "lock" ||
      component.BindValue === "lock" ||
      (component as any).onPressAction === "lock" ||
      (component as any).onPressAction === "unlock"
    ) {
      if (isScreenLocked) {
        (component as any).onPressAction = "unlock";
        // Optionally update label if it was "Lock"
        if (component.Label === "Lock" || component.Label === "lock") component.Label = "Unlock";
        if ((component as any).icon === "lock") (component as any).icon = "unlock";
      } else {
        (component as any).onPressAction = "lock";
        if (component.Label === "Unlock" || component.Label === "unlock") component.Label = "Lock";
        if ((component as any).icon === "unlock") (component as any).icon = "lock";
      }
    }

    // Field visibility from screen setting (form.fieldVisibilityRules): show fields only when condition matches
    const rules = parentFormConfig?.fieldVisibilityRules;
    if (rules != null && rules.length > 0 && formData) {
      const fieldName = (component as any).name ?? (component as any).BindValue;
      if (fieldName != null && typeof fieldName === "string") {
        const rule = rules.find((r: FieldVisibilityRule) =>
          r.showFields.includes(fieldName)
        );
        if (rule) {
          const { field: refField, label: requiredLabel } = rule.when;
          const req = String(requiredLabel).toLowerCase();
          const selectedData = formData[`${refField}_selectedData`] as Record<string, unknown> | undefined;
          const rawValue = formData[refField];
          const selectedLabel =
            selectedData != null
              ? String((selectedData.name ?? selectedData.label ?? selectedData.value ?? "")).toLowerCase()
              : "";
          const rawStr = rawValue != null ? String(rawValue).toLowerCase() : "";
          const isVisible = selectedLabel.includes(req) || rawStr.includes(req);
          if (!isVisible) return null;
        }
      }
    }

    // Layout Components
    if (
      component.componentType === LayoutTypes.SCREEN_GROUP ||
      component.componentType === LayoutTypes.GROUP ||
      component.componentType === LayoutTypes.GRID ||
      component.componentType === LayoutTypes.FORM ||
      (component as any).componentType === "OneAppAddEdit" ||
      component.componentType === LayoutTypes.FIELD_GROUP
    ) {
      const isGrid = component.componentType === LayoutTypes.GRID;
      const isForm = component.componentType === LayoutTypes.FORM || (component as any).componentType === "OneAppAddEdit";
      const isScreenGroup =
        component.componentType === LayoutTypes.SCREEN_GROUP;

      // Check if this SCREEN_GROUP has fabAction and contains SUMMARY_LIST
      const fabAction = isScreenGroup ? getFabAction(component) : null;
      const shouldShowFab = fabAction && hasSummaryList(component);
      const screenGroupTitle = isScreenGroup
        ? (component as ScreenGroupComponent).title
        : undefined;

      // Check if this component tree contains a submit button (for adding bottom padding)
      const hasSubmitButtonInTree = containsSubmitButton(component);

      // For FORM components, check if Fields property exists and convert to childComponents
      const formComponent = component as any;
      let childrenToRender = component.childComponents;
      if (isForm && formComponent.Fields && !formComponent.childComponents) {
        childrenToRender = convertFieldsToChildComponents(formComponent.Fields);
      }

      // Process formActions if present
      if (isForm && formComponent.formActions && Array.isArray(formComponent.formActions)) {
        const actionButtons = formComponent.formActions.map((action: any) => ({
          componentType: ComponentType.BUTTON,
          ...action,
          name: action.name,
          Label: action.Label || action.name || action.type,
          BindValue: action.BindValue || action.name || action.type,
          onPressAction: action.onPressAction,
          icon: action.icon || action.type,
          ActionType: action.ActionType || action.type,
          Settings: action.Settings || action.settings,
          settings: action.Settings || action.settings,
          style: {
            backgroundColor: "var(--primary-green)",
            textColor: "#FFFFFF",
            padding: 8,
            marginBottom: 8,
            borderRadius: 4,
            fontSize: 14,
            ...action.style,
          }
        }));

        const actionsGroup = {
          componentType: LayoutTypes.GROUP,
          BindValue: "cont_formActions",
          style: {
            direction: FlexDirection.ROW,
            padding: 0,
            margin: 0,
            marginBottom: 8,
          },
          childComponents: actionButtons
        };

        childrenToRender = childrenToRender ? [actionsGroup, ...childrenToRender] : [actionsGroup];
      }


      // Add bottom padding when submit button exists to prevent overlap
      // Only add to SCREEN_GROUP or FORM to avoid double padding
      const bottomPaddingStyle = (isScreenGroup || isForm) && hasSubmitButtonInTree
        ? { paddingBottom: '150px' }
        : {};

      // Pass form config down so children can use fieldVisibilityRules (screen setting)
      const parentForChildren = isForm ? (component as FormComponent) : parentFormConfig;

      return (
        <div
          key={key}
          style={{ ...getLayoutStyles(component), ...bottomPaddingStyle }}
          className={getLayoutClasses(component)}
          data-form-container={isForm ? "true" : undefined}
          data-screen-id={screenId}
        >
          {screenGroupTitle && (
            <div className="py-2 flex items-center gap-3 ">
              {previousView && (
                <button
                  onClick={() =>
                    dispatch(
                      navigateToView({ view: previousView, isBack: true })
                    )
                  }
                  className="flex items-center justify-center w-9 h-9 bg-white dark:!bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 active:scale-95 shadow-sm"
                  aria-label="Go back"
                  title="Go back"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-700 dark:text-gray-200"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 m-0 tracking-tight">
                {screenGroupTitle}
              </h1>
            </div>
          )}
          {shouldShowFab && renderFabButton(fabAction!)}
          {(() => {
            const renderedChildren =
              childrenToRender
                ?.filter((child) => {
                  // Filter out null, undefined, or empty components to avoid unwanted columns
                  if (!child) return false;
                  // Filter out components that are just empty objects or have no meaningful content
                  if (typeof child === "object" && Object.keys(child).length === 0) return false;
                  // Filter out components that don't have a componentType (invalid components)
                  if (typeof child === "object" && !(child as any).componentType) return false;
                  return true;
                })
                .map((child, childIndex) => renderComponent(child, childIndex, parentForChildren))
                // Important: visibility rules can make renderComponent return null; collapse empty wrappers.
                .filter(Boolean) ?? [];

            // If a layout container (GROUP/GRID/FIELD_GROUP) ends up empty (e.g., role-based visibility),
            // don't render an empty box that creates blank space.
            if (
              renderedChildren.length === 0 &&
              !isScreenGroup &&
              !isForm
            ) {
              return null;
            }

            return renderedChildren as any;
          })()}
        </div>
      );
    }

    if (component.componentType === ComponentType.CHECKBOX) {
      const fieldConfig = component as any;
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      let fieldValue = formData[fieldName];

      // Handle Json_Attribute for checkboxes in edit mode
      if (
        (fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === "") &&
        fieldConfig.Json_Attribute
      ) {
        fieldValue = formData[fieldConfig.Json_Attribute];
      }

      // Handle "False"/"True" strings from default value
      if (fieldValue === undefined && fieldConfig.defaultValue) {
        fieldValue = (fieldConfig.defaultValue === "True" || fieldConfig.defaultValue === "true" || fieldConfig.defaultValue === true);
        if (typeof fieldValue === 'string') fieldValue = fieldValue === "true";
      }

      return (
        <Checkbox
          key={key}
          config={fieldConfig}
          value={fieldValue}
          onChange={(name, value) => handleFormDataChange(name, value)}
        />
      );
    }

    if (component.componentType === ComponentType.BUTTON) {
      const buttonConfig = component as any;
      const isSubmit = isSubmitButton(component);

      // Wrap standalone submit buttons in a centered container
      // (when not already in a GROUP or GRID that handles centering)
      if (isSubmit) {
        const buttonLabel = buttonConfig.Label || buttonConfig.name || "SAVE";

        // Validation Logic
        const validateForm = (components: ComponentConfig[], data: Record<string, any>): Record<string, string> => {
          let errors: Record<string, string> = {};

          // Collect all field visibility rules from all FORM components in this screen
          const formVisibilityRules: FieldVisibilityRule[] = [];

          const collectFormVisibilityRules = (comps: ComponentConfig[]) => {
            comps.forEach((comp) => {
              const anyComp = comp as any;
              if (
                comp.componentType === LayoutTypes.FORM ||
                anyComp.componentType === "OneAppAddEdit"
              ) {
                if (Array.isArray(anyComp.fieldVisibilityRules)) {
                  formVisibilityRules.push(...(anyComp.fieldVisibilityRules as FieldVisibilityRule[]));
                }
              }
              if (anyComp.childComponents && Array.isArray(anyComp.childComponents)) {
                collectFormVisibilityRules(anyComp.childComponents);
              }
            });
          };

          collectFormVisibilityRules(components);

          const isFieldVisibleByRules = (fieldName: string | undefined | null): boolean => {
            if (!fieldName) return true;
            if (formVisibilityRules.length === 0) return true;

            const rule = formVisibilityRules.find((r) => r.showFields?.includes(fieldName));
            if (!rule) return true; // no rule => always visible

            const refField = rule.when.field;
            const requiredLabel = String(rule.when.label).toLowerCase();
            const selectedData = data[`${refField}_selectedData`] as Record<string, unknown> | undefined;
            const rawValue = data[refField];
            const selectedLabel =
              selectedData != null
                ? String((selectedData.name ?? selectedData.label ?? selectedData.value ?? "")).toLowerCase()
                : "";
            const rawStr = rawValue != null ? String(rawValue).toLowerCase() : "";
            const isVisible = selectedLabel.includes(requiredLabel) || rawStr.includes(requiredLabel);
            return isVisible;
          };

          const traverse = (comps: ComponentConfig[]) => {
            comps.forEach((comp) => {
              const compAny = comp as any;
              const type = comp.componentType as string;

              const fieldName = compAny.name || compAny.BindValue;

              // Validate Inputs
              if (
                type === ComponentType.TEXT_FIELD ||
                type === ComponentType.SELECT_DROPDOWN ||
                type === ComponentType.SEARCHABLE_DROPDOWN ||
                type === ComponentType.DATE_FIELD ||
                type === ComponentType.DATETIME_FIELD ||
                type === ComponentType.TEXT_AREA ||
                type === "money"
              ) {

                if (fieldName) {
                  const jsonAttr = compAny.Json_Attribute || compAny.json_Attribute;
                  let value = data[fieldName];
                  if (
                    (value === undefined || value === null || String(value).trim() === "") &&
                    jsonAttr
                  ) {
                    value = data[jsonAttr];
                  }
                  const label = compAny.label || compAny.Label || fieldName || "Field";

                  // If this field is controlled by fieldVisibilityRules and is currently hidden,
                  // skip all validations (required + custom rules)
                  if (!isFieldVisibleByRules(fieldName)) {
                    return;
                  }

                  // Required Check
                  const isRequired = compAny.required || compAny.IsMandatory;
                  if (isRequired) {
                    if (value === undefined || value === null || String(value).trim() === "") {
                      errors[fieldName] = `${label} is required`;
                    }
                  }

                  // Custom Validation Rules
                  if (!errors[fieldName] && compAny.validation && value !== undefined && value !== null && String(value).trim() !== "") {
                    compAny.validation.forEach((rule: any) => {
                      if (rule.rule === ValidationRule.PATTERN) {
                        const regex = new RegExp(rule.value as string);
                        if (!regex.test(String(value))) {
                          errors[fieldName] = rule.message || `${label} is invalid`;
                        }
                      } else if (rule.rule === ValidationRule.EMAIL) {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(String(value))) {
                          errors[fieldName] = rule.message || `${label} is invalid email`;
                        }
                      } else if (rule.rule === ValidationRule.MIN_LENGTH) {
                        if (String(value).length < (rule.value as number)) {
                          errors[fieldName] = rule.message || `${label} must be at least ${rule.value} chars`;
                        }
                      } else if (rule.rule === ValidationRule.MAX_LENGTH) {
                        if (String(value).length > (rule.value as number)) {
                          errors[fieldName] = rule.message || `${label} must be at most ${rule.value} chars`;
                        }
                      }
                    });
                  }
                }
              }

              // Validate SubModule (Min Rows, Required, and per-row conditional validations)
              if (comp.componentType === ComponentType.SUB_MODULE) {
                if (fieldName) {
                  const value = data[fieldName]; // Should be array
                  const label = compAny.label || compAny.Label || compAny.TableName || "Table";

                  // Check for NotAllowEmpty
                  if (compAny.NotAllowEmpty) {
                    if (!value || !Array.isArray(value) || value.length === 0) {
                      errors[fieldName] = compAny.errorText || `${label} requires at least one entry`;
                    }
                  }

                  if (compAny.validation) {
                    compAny.validation.forEach((rule: any) => {
                      if (rule.rule === ValidationRule.REQUIRED) {
                        if (!value || !Array.isArray(value) || value.length === 0) {
                          errors[fieldName] = rule.message || `${label} requires at least one entry`;
                        }
                      }
                      if (rule.rule === ValidationRule.MIN_VALUE && value && Array.isArray(value)) {
                        if (value.length < (rule.value as number)) {
                          errors[fieldName] = rule.message || `${label} requires ${rule.value} entries`;
                        }
                      }
                    });
                  }
                  // Run expression-based conditional validations per row (e.g. [qty] >= [issued_qty])
                  if (value && Array.isArray(value) && value.length > 0 && compAny.childControls?.length) {
                    const conditionalResults = runConditionalValidations(value, compAny.childControls);
                    if (conditionalResults.length > 0) {
                      // Format messages with proper spacing for better parsing
                      const messages = conditionalResults.map((r) => `Row ${r.rowIndex}: ${r.message}`).join(" ");
                      errors[fieldName] = errors[fieldName] ? `${errors[fieldName]} ${messages}` : messages;
                    }
                  }

                  // Run CONDITION validations declared on FooterControls.
                  // Footer aggregate values (e.g. total_debit, total_credit) are stored in
                  // top-level formData keyed by their Json_Attribute, so we evaluate the
                  // condition against `data` (formData) directly instead of per-row data.
                  if (!errors[fieldName] && compAny.FooterControls?.length) {
                    for (const footerCtrl of compAny.FooterControls) {
                      const footerValidation = footerCtrl.validation;
                      if (!Array.isArray(footerValidation)) continue;
                      for (const rule of footerValidation) {
                        if (rule.rule !== ValidationRule.CONDITION || !rule.condition) continue;
                        // Resolve [ref] tokens against formData using Json_Attribute / BindValue / name
                        let expr: string = rule.condition;
                        const refRegex = /\[[\w]+\]/g;
                        const refs: string[] = [...new Set<string>(rule.condition.match(refRegex) || [])];
                        for (const ref of refs) {
                          const cleanRef = ref.replace(/[\[\]]/g, "").trim();
                          // Try to find the footer control whose Json_Attribute or BindValue matches
                          let resolvedValue: string | undefined;
                          for (const fc of compAny.FooterControls) {
                            const jsonAttr = fc.Json_Attribute || fc.json_Attribute;
                            const bindVal = fc.BindValue || fc.name;
                            if (jsonAttr === cleanRef || bindVal === cleanRef) {
                              const raw = data[jsonAttr] ?? data[bindVal] ?? data[cleanRef];
                              if (raw !== undefined && raw !== null) {
                                resolvedValue = String(raw);
                              }
                              break;
                            }
                          }
                          // Fallback: direct key lookup in formData
                          if (resolvedValue === undefined) {
                            const raw = data[cleanRef];
                            if (raw !== undefined && raw !== null) resolvedValue = String(raw);
                          }
                          const num = resolvedValue !== undefined && resolvedValue !== "" ? parseFloat(resolvedValue) : NaN;
                          const replacement = Number.isFinite(num) ? String(num) : (resolvedValue !== undefined ? `"${resolvedValue}"` : "0");
                          expr = expr.split(ref).join(replacement);
                        }
                        // Safety check: only allow arithmetic / comparison characters
                        if (!/^[\d.\s+\-*/()\<=\>!="]+$/.test(expr.trim())) continue;
                        try {
                          const ok = Boolean(new Function(`return (${expr})`)());
                          if (!ok && rule.message) {
                            errors[fieldName] = rule.message;
                            break;
                          }
                        } catch {
                          // ignore evaluation errors
                        }
                      }
                      if (errors[fieldName]) break;
                    }
                  }

                  // Check Required / IsMandatory per column per row (only on submit)
                  if (!errors[fieldName] && value && Array.isArray(value) && compAny.childControls?.length) {
                    const activeRows = value.filter((r: any) => r.soft_delete !== 1 && r.soft_delete !== "1");
                    for (let rowIdx = 0; rowIdx < activeRows.length; rowIdx++) {
                      const row = activeRows[rowIdx];
                      for (const ctrl of compAny.childControls) {
                        const isRequired = ctrl.Required === true || ctrl.IsMandatory === true;
                        if (!isRequired) continue;
                        const ctrlFieldName = ctrl.name || ctrl.BindValue;
                        const jsonAttr = ctrl.Json_Attribute;
                        const cellValue =
                          (ctrlFieldName !== undefined && row[ctrlFieldName] !== undefined && row[ctrlFieldName] !== null && row[ctrlFieldName] !== "")
                            ? row[ctrlFieldName]
                            : (jsonAttr !== undefined && row[jsonAttr] !== undefined && row[jsonAttr] !== null && row[jsonAttr] !== "")
                            ? row[jsonAttr]
                            : null;
                        if (cellValue === null || cellValue === undefined || String(cellValue).trim() === "") {
                          const ctrlLabel = ctrl.Label || ctrl.label || ctrlFieldName || "Field";
                          errors[fieldName] = `Row ${rowIdx + 1}: "${ctrlLabel}" is required`;
                          break;
                        }
                      }
                      if (errors[fieldName]) break;
                    }
                  }
                }
              }

              // Recursion
              if (comp.childComponents) traverse(comp.childComponents);
              if (compAny.Fields) traverse(convertFieldsToChildComponents(compAny.Fields));

              // CRITICAL FIX: Do NOT traverse childControls for SUB_MODULE or MULTI_SUB_LIST
              // These define columns for internal data, not top-level form fields
              if (
                compAny.childControls &&
                comp.componentType !== ComponentType.SUB_MODULE &&
                comp.componentType !== ComponentType.MULTI_SUB_LIST
              ) {
                traverse(compAny.childControls);
              }
            });
          };

          traverse(components);
          return errors;
        };

        const handleClick = () => {
          // Perform Validation
          const errors = validateForm(config, formData);



          if (Object.keys(errors).length > 0) {
            setFormErrors(errors);

            // Show validation popup with formatted error messages
            const errorMessages = Object.values(errors);
            // Join all error messages with proper spacing for parsing
            const popupMessage = errorMessages.join(" ");
            dispatch(
              showConfirmationDialog({
                message: popupMessage,
                title: "Validation",
                resolve: () => { },
              })
            );

            // Scroll to first error
            const firstErrorField = Object.keys(errors)[0];
            setTimeout(() => {
              const errorElement = document.querySelector(`[data-field-name="${firstErrorField}"]`);
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 150);

            return; // Block execution
          }

          setFormErrors({}); // Clear errors if valid

          if (buttonConfig.onPressAction && handleActionTrigger) {
            handleActionTrigger(buttonConfig.onPressAction, { buttonConfig });
          }
        };

        // Render submit button. On full pages we portal/fix it so it's always visible.
        // In popup mode, keep it inside the modal content area.
        const buttonElement = (
          <div
            key={key}
            className={
              renderMode === "popup"
                ? "sticky bottom-4 w-full flex justify-end z-50 mt-4"
                : "fixed bottom-10 right-10 z-[99999] opacity-100"
            }
            style={
              renderMode === "popup"
                ? { pointerEvents: "auto" }
                : { position: "fixed", pointerEvents: "auto", bottom: "2.5rem", right: "2.5rem" }
            }
          >
            <button
              data-submit-button="true"
              data-action-id={buttonConfig.onPressAction}
              className="submit-button-theme submit-button-ring group flex items-center gap-2 text-white pl-6 pr-8 py-3 rounded-full shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:scale-105 cursor-pointer ring-2 ring-transparent disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100"
              onClick={handleClick}
              disabled={buttonConfig.disabled || buttonConfig.loading || isSaving}
            >
              <div className="bg-white/20 rounded-full p-1.5 group-hover:bg-white/30 transition-colors">
                {buttonConfig.loading || isSaving ? (
                  <div className="w-6 h-6 border-2 border-transparent border-t-current rounded-full animate-spin" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                )}
              </div>

              <div className="flex flex-col items-start">
                <span className="font-bold tracking-wide text-base leading-none">{buttonLabel.toUpperCase()}</span>
                <span className="submit-button-subtitle text-[10px] font-medium opacity-80 group-hover:opacity-100">Click to confirm</span>
              </div>
            </button>
          </div>
        );

        // Use portal to render button directly to document.body to ensure it's always visible (page mode only)
        if (renderMode !== "popup" && typeof document !== "undefined") {
          return createPortal(buttonElement, document.body);
        }

        return buttonElement;
      }

      // Create a wrapper that passes button config along with actionId
      const handleButtonClick = (actionId: string, config?: ButtonComponent) => {
        if (handleActionTrigger) {
          handleActionTrigger(actionId, { buttonConfig: config || buttonConfig });
        }
      };

      return (
        <Button key={key} config={buttonConfig} onClick={handleButtonClick} />
      );
    }

    if (component.componentType === ComponentType.INFO_CARD) {
      return <InfoCard key={key} config={component as any} />;
    }

    if (component.componentType === ComponentType.CHECK_IN_OUT_COMPONENT) {
      return (
        <CheckInOut
          key={key}
          config={component as any}
          onActionTrigger={handleActionTrigger}
        />
      );
    }

    if (component.componentType === ComponentType.INFO_CARDS) {
      const infoCardsConfig = component as any; // InfoCardsComponent
      
      if (infoCardsConfig.apiname) {
        return (
          <InfoCardsWithStats
            key={key}
            screenId={screenId}
            infoCardsConfig={infoCardsConfig}
            formData={formData}
          />
        );
      }

      // Fallback icons for common HRMS stat cards.
      // Used when the DB-stored icon is a raw bind_value key or a garbled/corrupted emoji.
      const _iconFallbacks: Record<string, string> = {
        present_days:          "✅",
        absent_days:           "❌",
        late_arrivals:         "⏰",
        total_leaves:          "📋",
        pending:               "⏳",
        pending_leaves:        "⏳",
        approved:              "✔️",
        approved_leaves:       "✔️",
        rejected:              "🚫",
        rejected_leaves:       "🚫",
        total_payout:          "💰",
        pending_approvals:     "⏳",
        employees_processed:   "👥",
      };
      const _defaultColors: Record<string, string> = {
        present_days:          "#34D399",
        absent_days:           "#F97316",
        late_arrivals:         "#FACC15",
        total_leaves:          "#60A5FA",
        pending:               "#FACC15",
        pending_leaves:        "#F59E0B",
        approved:              "#34D399",
        approved_leaves:       "#10B981",
        rejected:              "#F87171",
        rejected_leaves:       "#EF4444",
        total_payout:          "#34D399",
        pending_approvals:     "#FACC15",
        employees_processed:   "#60A5FA",
      };

      /**
       * Return true when the icon string should be replaced with a fallback:
       *  - empty / whitespace
       *  - plain bind_value key like "PRESENT_DAYS"
       *  - garbled / corrupted bytes (contains '?' which replaces unknown chars)
       */
      const _isBadIcon = (s: any): boolean => {
        if (typeof s !== "string" || s.trim() === "") return true;
        if (/^[A-Z0-9_]+$/.test(s)) return true;       // bind_value key
        if (s.includes("?")) return true;               // garbled / corrupted emoji
        return false;
      };

      /** White or missing colors need a fallback */
      const _isBadColor = (c: any): boolean =>
        !c || c === "#ffffff" || c === "#FFFFFF" || c === "white";

      // Map config cards to HrmStatCard format with values from formData
      const stats = infoCardsConfig.cards.map((card: any) => {
        const value = formData[card.bindValue] !== undefined
          ? formData[card.bindValue]
          : (card.defaultValue || 0);

        const key = (card.bindValue || "").toLowerCase();
        const icon  = _isBadIcon(card.icon)  ? (_iconFallbacks[key]  ?? "📊") : card.icon;
        const color = _isBadColor(card.color) ? (_defaultColors[key] ?? "#3B82F6") : card.color;

        return {
          id: card.bindValue || card.name,
          label: card.name,
          value: value,
          icon,
          accentColor: color,
          description: card.description || "",
        };
      });

      return (
        <HrmStatCards
          key={key}
          stats={stats}
          variant="default"
        />
      );
    }

    if (component.componentType === ComponentType.LEAVE_BALANCE) {
      return (
        <LeaveBalance
          key={key}
          title={(component as any).title}
          config={component as any}
          leaveBalances={(component as any).leaveBalances}
          apiname={(component as any).apiname}
        />
      );
    }

    if (component.componentType === ComponentType.CUSTOM) {
      const customConfig = component as any;
      if (customConfig.customType === "dateFilter") {
        const fieldName = customConfig.name || customConfig.BindValue || "date";
        // Handle value from formData
        let fieldValue = formData[fieldName];

        // Handle Json_Attribute
        if (
          (fieldValue === undefined || fieldValue === null || fieldValue === "") &&
          customConfig.Json_Attribute
        ) {
          fieldValue = formData[customConfig.Json_Attribute];
        }

        return (
          <DateFilter
            key={key}
            config={customConfig}
            value={fieldValue}
            // Pass formData change handler
            onFormDataChange={(name, value) => {
              handleFormDataChange(name, value);
              // Also trigger action if defined (e.g. for filtering)
              if (customConfig.onPressAction) {
                // Small delay to ensure state updates
                setTimeout(() => {
                  handleActionTrigger(customConfig.onPressAction, { [name]: value });
                }, 0);
              }
            }}
            // Pass raw formData for flexibility
            formData={formData}
          />
        );
      }
    }

    // Form Field Components
    if (component.componentType === ComponentType.TEXT_FIELD) {
      const fieldConfig = component as any;
      // Handle both old format (BindValue) and new format (name)
      const fieldName = fieldConfig.name || fieldConfig.BindValue;

      // Evaluate ExpressionV2 if present (for calculated fields like Gross Amount, VAT Amount, Total Amount)
      let fieldValue = formData[fieldName];
      const expressionV2 = fieldConfig.ExpressionV2;

      if (expressionV2 && typeof expressionV2 === "string") {
        try {
          // Helper function to resolve cross-sub-module references: [(submodule).field]
          const resolveCrossSubModuleReference = (match: string): string => {
            const crossRefMatch = match.match(/\[\(([^)]+)\)\.([^\]]+)\]/);
            if (crossRefMatch) {
              const subModuleName = crossRefMatch[1]; // e.g., "service_spare_est_req"
              const fieldBindValue = crossRefMatch[2]; // e.g., "txt_sub_amount"

              // Find the SubModule component in config to get its Json_Attribute mapping
              const findSubModuleConfig = (comps: ComponentConfig[]): any => {
                for (const comp of comps) {
                  const compAny = comp as any;
                  if (comp.componentType === ComponentType.SUB_MODULE) {
                    // Check multiple properties: name, Name, TableName
                    if (comp.name === subModuleName ||
                      compAny.Name === subModuleName ||
                      compAny.TableName === subModuleName) {
                      return comp;
                    }
                  }
                  // CRITICAL: Check Fields property (used by FORM components)
                  if (compAny.Fields && Array.isArray(compAny.Fields)) {
                    const found = findSubModuleConfig(compAny.Fields);
                    if (found) return found;
                  }
                  // Check childComponents (used by GROUP, GRID, etc.)
                  if (comp.childComponents && Array.isArray(comp.childComponents)) {
                    const found = findSubModuleConfig(comp.childComponents);
                    if (found) return found;
                  }
                  // Check childControls (used by SubModule itself)
                  if (compAny.childControls && Array.isArray(compAny.childControls)) {
                    const found = findSubModuleConfig(compAny.childControls);
                    if (found) return found;
                  }
                }
                return null;
              };

              const subModuleConfig = findSubModuleConfig(config);

              if (subModuleConfig) {
                // Find the Json_Attribute for the field bindValue in FooterControls
                const footerControls = (subModuleConfig as any).FooterControls || [];
                const footerField = footerControls.find((fc: any) =>
                  (fc.name || fc.BindValue) === fieldBindValue
                );

                if (footerField) {
                  // Use Json_Attribute to get the value from formData
                  const jsonAttribute = footerField.Json_Attribute || footerField.json_Attribute || fieldBindValue;
                  const value = formData[jsonAttribute];



                  // Treat 0 as a valid value (not empty)
                  if (value !== undefined && value !== null && value !== "") {
                    const numValue = parseFloat(String(value));
                    return String(isNaN(numValue) ? 0 : numValue);
                  }
                  // Also check if value is explicitly 0
                  if (value === 0 || value === "0") {
                    return "0";
                  }

                } else if (import.meta.env.DEV) {
                  // Helper to find all SubModules in config for debugging
                  const findAllSubModules = (comps: ComponentConfig[]): any[] => {
                    const subModules: any[] = [];
                    const walk = (components: ComponentConfig[]) => {
                      components.forEach((comp: any) => {
                        if (comp.componentType === ComponentType.SUB_MODULE) {
                          subModules.push({
                            name: comp.name,
                            Name: comp.Name,
                            TableName: comp.TableName,
                            FooterControls: comp.FooterControls?.map((fc: any) => ({
                              bindValue: fc.name || fc.BindValue,
                              jsonAttribute: fc.Json_Attribute || fc.json_Attribute,
                            })) || [],
                          });
                        }
                        if (comp.Fields && Array.isArray(comp.Fields)) {
                          walk(comp.Fields);
                        }
                        if (comp.childComponents && Array.isArray(comp.childComponents)) {
                          walk(comp.childComponents);
                        }
                        if (comp.childControls && Array.isArray(comp.childControls)) {
                          walk(comp.childControls);
                        }
                      });
                    };
                    walk(comps);
                    return subModules;
                  };


                }
              }

              // Fallback: try multiple variations to find the value
              // PRIORITIZE Json_Attribute mappings first (these are what SubModule actually stores)
              const possibleKeys = [
                // Direct Json_Attribute mappings (most reliable - these are what SubModule stores)
                subModuleName === "service_spare_est_req" && fieldBindValue === "txt_sub_amount" ? "spare_amount" : null,
                subModuleName === "service_spare_est_req" && fieldBindValue === "spare_vat_amount" ? "spare_vat_amount" : null,
                subModuleName === "service_spare_est_req" && fieldBindValue === "txt_sub_net_amount" ? "spare_net_amount" : null,

                subModuleName === "service_invoice_spareparts" && fieldBindValue === "txt_sub_amount" ? "spare_amount" : null,
                subModuleName === "service_invoice_spareparts" && fieldBindValue === "spare_vat_amount" ? "spare_vat_amount" : null,
                subModuleName === "service_invoice_spareparts" && fieldBindValue === "txt_sub_net_amount" ? "spare_net_amount" : null,

                subModuleName === "service_labour_est" && fieldBindValue === "txt_sub_amount" ? "labour_amount" : null,
                subModuleName === "service_labour_est" && fieldBindValue === "labour_vat_amount" ? "labour_vat_amount" : null,
                subModuleName === "service_labour_est" && fieldBindValue === "txt_sub_net_amount" ? "labour_net_amount" : null,

                subModuleName === "service_invoice_labour" && fieldBindValue === "txt_sub_amount" ? "labour_amount" : null,
                subModuleName === "service_invoice_labour" && fieldBindValue === "labour_vat_amount" ? "labour_vat_amount" : null,
                subModuleName === "service_invoice_labour" && fieldBindValue === "txt_sub_net_amount" ? "labour_net_amount" : null,

                subModuleName === "estimation_miscellaneous" && fieldBindValue === "txt_sub_amount" ? "miscellaneous_amount" : null,
                subModuleName === "estimation_miscellaneous" && fieldBindValue === "miscellanious_vat_amount" ? "miscellanious_vat_amount" : null,
                subModuleName === "estimation_miscellaneous" && fieldBindValue === "txt_sub_net_amount" ? "miscellaneous_net_amount" : null,

                subModuleName === "service_invoice_miscellaneous" && fieldBindValue === "txt_sub_amount" ? "miscellaneous_amount" : null,
                subModuleName === "service_invoice_miscellaneous" && fieldBindValue === "miscellanious_vat_amount" ? "miscellanious_vat_amount" : null,
                subModuleName === "service_invoice_miscellaneous" && fieldBindValue === "txt_sub_net_amount" ? "miscellaneous_net_amount" : null,

                // Then try BindValue variations
                fieldBindValue,
                `${subModuleName}_${fieldBindValue}`,
              ].filter(Boolean) as string[];

              for (const key of possibleKeys) {
                const fallbackValue = formData[key];
                // Check if value exists (including 0 as valid)
                if (fallbackValue !== undefined && fallbackValue !== null) {
                  // Handle empty string separately
                  if (fallbackValue === "") {
                    continue; // Skip empty strings, try next key
                  }
                  // Value exists (including 0)
                  const numValue = typeof fallbackValue === "number"
                    ? fallbackValue
                    : parseFloat(String(fallbackValue));



                  return String(isNaN(numValue) ? 0 : numValue);
                }
              }



              return "0";
            }
            return match;
          };

          // Replace cross-sub-module references: [(submodule).field]
          let expression = expressionV2;
          // Use a map to track resolved values to avoid re-resolving the same reference
          const resolvedCrossRefs = new Map<string, string>();
          const crossRefRegex = /\[\([^)]+\)\.[^\]]+\]/g;
          // Use match() instead of exec() to avoid regex state issues
          const crossRefMatches = expression.match(crossRefRegex) || [];
          const uniqueMatches = [...new Set(crossRefMatches)] as string[]; // Remove duplicates
          uniqueMatches.forEach((matchStr: string) => {
            if (!resolvedCrossRefs.has(matchStr)) {
              resolvedCrossRefs.set(matchStr, resolveCrossSubModuleReference(matchStr));
            }
          });
          // Replace all occurrences (use global regex since replaceAll isn't available)
          resolvedCrossRefs.forEach((resolved, matchStr) => {
            // Escape special regex characters in matchStr
            const escapedMatch = matchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(escapedMatch, 'g');
            expression = expression.replace(regex, resolved);
          });

          // Replace regular field references: [field] (support time strings HH:mm for duration expressions)
          const fieldRefRegex = /\[(\w+)\]/g;
          const fieldMatches = expression.match(fieldRefRegex) || [];
          for (const match of fieldMatches) {
            const fieldRef = match.replace(/[\[\]]/g, "");
            const refValue = formData[fieldRef];
            const timeHours = timeStringToDecimalHours(refValue);
            const safeValue =
              timeHours !== null
                ? String(timeHours)
                : (refValue !== undefined && refValue !== null && refValue !== "")
                  ? String(parseFloat(String(refValue)) || 0)
                  : "0";
            expression = expression.replace(match, safeValue);
          }

          // Replace lookup property references: [lookup.property]
          const lookupPropRegex = /\[(\w+)\.(\w+)\]/g;
          const lookupMatches = expression.match(lookupPropRegex) || [];
          for (const match of lookupMatches) {
            const lookupMatch = match.match(/\[(\w+)\.(\w+)\]/);
            if (lookupMatch) {
              const lookupFieldName = lookupMatch[1];
              const property = lookupMatch[2];
              const lookupValue = formData[lookupFieldName];

              // Try to get property from selectedData or lookup item
              const selectedDataKey = `${lookupFieldName}_selectedData`;
              const selectedData = formData[selectedDataKey];

              if (selectedData && selectedData[property] !== undefined) {
                expression = expression.replace(match, String(parseFloat(String(selectedData[property])) || 0));
              } else if (lookupValue && typeof lookupValue === "object" && lookupValue[property] !== undefined) {
                expression = expression.replace(match, String(parseFloat(String(lookupValue[property])) || 0));
              } else {
                expression = expression.replace(match, "0");
              }
            }
          }

          // Evaluate the expression
          try {
            const evaluatedValue = Function(`"use strict"; return (${expression})`)();



            if (evaluatedValue !== null && evaluatedValue !== undefined && !isNaN(evaluatedValue)) {
              // Format to 2 decimal places for currency/numeric fields
              const formattedValue = typeof evaluatedValue === "number"
                ? evaluatedValue.toFixed(2)
                : String(evaluatedValue);
              fieldValue = formattedValue;

              // Update formData with calculated value (but don't trigger onChange to avoid loops)
              if (formData[fieldName] !== fieldValue) {
                // Use a ref to prevent infinite loops
                const updateKey = `${fieldName}_${expressionV2}`;
                if (!previousCalculatedValuesRef.current.has(updateKey) ||
                  previousCalculatedValuesRef.current.get(updateKey) !== fieldValue) {
                  previousCalculatedValuesRef.current.set(updateKey, fieldValue);
                  // Use setTimeout to avoid updating during render
                  setTimeout(() => {
                    handleFormDataChange(fieldName, fieldValue);
                  }, 0);
                }
              }
            } else {
              // If evaluation failed, try to use existing formData value or default
              fieldValue = formData[fieldName] || fieldConfig.defaultValue || "0";
            }
          } catch (evalError) {

            // Fallback to existing value or default
            fieldValue = formData[fieldName] || fieldConfig.defaultValue || "0";
          }
        } catch (error) {

          // Fallback to default value or existing value
          fieldValue = fieldValue || fieldConfig.defaultValue || "";
        }
      } else {
        // No expression, use formData value or default
        // Only checking for undefined or null, allowing empty strings
        if (
          (fieldValue === undefined ||
            fieldValue === null) &&
          fieldConfig.Json_Attribute
        ) {
          fieldValue = formData[fieldConfig.Json_Attribute];
        }

        // Only use defaultValue if fieldValue is still undefined or null
        // This allows empty string "" to be a valid value (user cleared the field)
        if (fieldValue === undefined || fieldValue === null) {
          fieldValue = fieldConfig.defaultValue || "";
        }
      }

      // Hidden data field — participates in fieldMappings/save but renders nothing visible
      if ((fieldConfig.style as any)?.display === "none") {
        return (
          <input
            key={key}
            type="hidden"
            name={fieldName}
            value={fieldValue || ""}
            onChange={(e) => handleFormDataChange(fieldName, e.target.value)}
          />
        );
      }

      return (
        <TextField
          key={key}
          config={fieldConfig}
          value={fieldValue}
          onChange={handleFormDataChange}
          externalError={formErrors[fieldName]}
        />
      );
    }

    if (component.componentType === ComponentType.TEXT_AREA) {
      const fieldConfig = component as any;
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      let fieldValue = formData[fieldName];
      if (
        (fieldValue === undefined ||
          fieldValue === null) &&
        fieldConfig.Json_Attribute
      ) {
        fieldValue = formData[fieldConfig.Json_Attribute];
      }
      return (
        <TextArea
          key={key}
          config={fieldConfig}
          value={fieldValue !== undefined && fieldValue !== null ? fieldValue : (fieldConfig.defaultValue || "")}
          onChange={handleFormDataChange}
          externalError={formErrors[fieldName]}
        />
      );
    }

    if (component.componentType === ComponentType.RADIO_FIELD) {
      const fieldConfig = component as any;
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      let fieldValue = formData[fieldName];
      if (
        (fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === "") &&
        fieldConfig.Json_Attribute
      ) {
        fieldValue = formData[fieldConfig.Json_Attribute];
      }
      return (
        <RadioField
          key={key}
          config={fieldConfig}
          value={fieldValue ?? fieldConfig.defaultValue}
          onChange={handleFormDataChange}
        />
      );
    }

    if (component.componentType === ComponentType.SELECT_DROPDOWN) {
      const fieldConfig = component as any;
      // Handle both old format (LookupName) and new format (lookupName)
      const lookupName = fieldConfig.lookupName || fieldConfig.LookupName;
      // Handle both old format (BindParent) and new format (parentField)
      const parentField =
        fieldConfig.parentField ||
        (fieldConfig.BindParent
          ? Object.keys(fieldConfig.BindParent)[0]
          : undefined);

      // Check if this is a workflow state field
      // Find form component to get Workflow property
      let workflowName: string | undefined;
      const findFormComponent = (comps: ComponentConfig[]): any => {
        for (const comp of comps) {
          if (comp.componentType === LayoutTypes.FORM) {
            return comp;
          }
          const compAny = comp as any;
          if (compAny.Fields) {
            const found = findFormComponent(compAny.Fields);
            if (found) return found;
          }
          if (comp.childComponents) {
            const found = findFormComponent(comp.childComponents);
            if (found) return found;
          }
        }
        return null;
      };
      const formComponent = findFormComponent(config);
      workflowName = formComponent?.Workflow || formComponent?.workflow;

      // Get options from lookup if lookupName is provided
      let options = fieldConfig.options || [];
      if (lookupName) {
        // Check if this is a workflow state (has Workflow property and LookupName matches workflow Action)
        if (workflowName) {
          // Use workflow states - check if already loaded in lookupsState
          const workflowLookupKey = `workflow_${workflowName}_${lookupName}`;
          const workflowOptions = lookupsState.lookups[workflowLookupKey] || [];
          if (workflowOptions.length > 0) {
            options = workflowOptions;
          } else {
            // CRITICAL: If field has a value (edit mode), fetch workflow options immediately
            // This ensures options are available when the component renders
            const fieldName = fieldConfig.name || fieldConfig.BindValue;
            const jsonAttr = fieldConfig.Json_Attribute;
            const fieldValue = formData[fieldName] || formData[jsonAttr];
            const hasValue = fieldValue !== undefined && fieldValue !== null && fieldValue !== "";

            // Fetch workflow if not already loading
            if (!lookupsState.loading[workflowLookupKey] && !requestedLookupsRef.current.has(workflowLookupKey)) {
              requestedLookupsRef.current.add(workflowLookupKey);



              // Fetch workflow and extract states
              (async () => {
                try {
                  const workflow = await apiService.getWorkflow(workflowName);
                  if (workflow && workflow.States && Array.isArray(workflow.States)) {
                    // Find workflow state where Action === lookupName
                    const workflowState = workflow.States.find(
                      (state: any) => state.Action === lookupName
                    );
                    if (workflowState && workflowState.States && Array.isArray(workflowState.States)) {
                      // Convert States array to options
                      const workflowOptions = workflowState.States.map((state: string) => ({
                        id: state,
                        name: state,
                        value: state,
                        label: state,
                      }));
                      // Store in lookupsState using addLookupItem for each option
                      workflowOptions.forEach((option: any) => {
                        dispatch(
                          addLookupItem({
                            lookupKey: workflowLookupKey,
                            item: option,
                          })
                        );
                      });


                    }
                  }
                } catch (error) {

                } finally {
                  requestedLookupsRef.current.delete(workflowLookupKey);
                }
              })();
            }
          }
        } else if (parentField) {
          // For dependent lookups, use parent value to get the right options
          const parentValue = formData[parentField];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            const lookupKey = `${lookupName}_${parentValue}`;
            options = lookupsState.lookups[lookupKey] || [];
          } else {
            options = []; // No options if parent is not selected
          }
        } else {
          // For independent lookups
          const lookupKey = lookupName;
          options = lookupsState.lookups[lookupKey] || [];

          // Log for get_all_ledgers

        }
      }

      // Ensure options is always an array
      const safeOptions = Array.isArray(options) ? options : [];

      // Create updated config with fetched options
      const updatedConfig = {
        ...fieldConfig,
        options: safeOptions,
        lookupName: lookupName,
        parentField: parentField,
      };

      // Get value from formData - try both field name and Json_Attribute
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      const jsonAttr = fieldConfig.Json_Attribute;
      let fieldValue = formData[fieldName];

      // If value not found by field name, try Json_Attribute
      if (
        (fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === "") &&
        jsonAttr
      ) {
        fieldValue = formData[jsonAttr];
      }

      // Log for workflow state fields (approve_status)


      // CRITICAL: Ensure workflow state value is correctly set
      // If this is a workflow state field and we have a value but it's not matching options,
      // try to find it by case-insensitive match
      if (workflowName && lookupName && fieldValue && options.length > 0) {
        const matchingOption = options.find((opt: any) =>
          String(opt.value).toLowerCase() === String(fieldValue).toLowerCase()
        );
        if (matchingOption && String(matchingOption.value) !== String(fieldValue)) {
          // Value exists but case doesn't match - use the option's value format
          const normalizedValue = matchingOption.value;
          fieldValue = normalizedValue;

          // CRITICAL: Update formData with normalized value to ensure it persists
          if (onFormDataChange) {
            // Update both fieldName and jsonAttr to ensure consistency
            if (fieldName) {
              onFormDataChange(fieldName, normalizedValue);
            }
            if (jsonAttr && jsonAttr !== fieldName) {
              onFormDataChange(jsonAttr, normalizedValue);
            }
          }


        }
      }

      // Fall back to defaultValue if still no value
      if (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === ""
      ) {
        fieldValue = getDefaultValue(fieldConfig);

        // CRITICAL: If default value is a label (like "5%") and this is a lookup dropdown,
        // convert it to the ID before storing
        if (fieldValue && lookupName && options.length > 0) {
          // Check if the default value matches a label instead of an ID
          const matchingOption = options.find(
            (opt: any) =>
              opt.label === fieldValue ||
              String(opt.label) === String(fieldValue) ||
              (opt as any).name === fieldValue ||
              String((opt as any).name) === String(fieldValue)
          );

          if (matchingOption) {
            // Convert label to ID
            fieldValue = matchingOption.value || (matchingOption as any).id || fieldValue;


          }
        }
      }

      // Create onFocus handler to refresh lookup data when dropdown opens
      // Always refresh to ensure latest data is fetched
      const handleFocus = () => {
        if (fieldConfig.lookupName) {
          if (fieldConfig.parentField) {
            // Dependent lookup - refresh if parent value exists
            const parentValue = formData[fieldConfig.parentField];
            if (
              parentValue !== undefined &&
              parentValue !== null &&
              parentValue !== ""
            ) {
              const lookupKey = `${fieldConfig.lookupName}_${parentValue}`;
              // Refresh dependent lookup to get latest data, but only if not already loading or requested
              if (
                !lookupsState.loading[lookupKey] &&
                !requestedLookupsRef.current.has(lookupKey)
              ) {
                requestedLookupsRef.current.add(lookupKey);

                dispatch(
                  fetchDependentLookup({
                    lookupName: fieldConfig.lookupName,
                    parentValue,
                    lookuptype: "Select",
                  })
                )
                  .then(() => {
                    requestedLookupsRef.current.delete(lookupKey);
                  })
                  .catch(() => {
                    requestedLookupsRef.current.delete(lookupKey);
                  });
              }
            }
          }
          // Note: Independent lookups are handled by the useEffect hook above
          // to avoid duplicate fetches
        }
      };

      // Log for get_all_ledgers dropdown


      return (
        <SelectDropdown
          key={key}
          config={updatedConfig}
          value={fieldValue}
          onChange={handleFormDataChange}
          onFocus={handleFocus}
          externalError={formErrors[fieldName]}
        />
      );
    }

    if (component.componentType === ComponentType.SEARCHABLE_DROPDOWN) {
      const fieldConfig = component as any;
      // Handle both old format (BindValue) and new format (name)
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      // Handle both old format (LookupName) and new format (lookupName)
      const lookupName = fieldConfig.lookupName || fieldConfig.LookupName;

      // Get options from lookup if lookupName is provided
      let options = fieldConfig.options || [];
      if (lookupName) {
        if (fieldConfig.parentField) {
          // For dependent lookups, use parent value to get the right options
          const parentValue = formData[fieldConfig.parentField];
          if (
            parentValue !== undefined &&
            parentValue !== null &&
            parentValue !== ""
          ) {
            const lookupKey = `${lookupName}_${parentValue}`;
            options = lookupsState.lookups[lookupKey] || [];
          } else {
            options = []; // No options if parent is not selected
          }
        } else {
          // For independent lookups
          const lookupKey = lookupName;
          options = lookupsState.lookups[lookupKey] || [];

          // Debug logging for inspection_items

        }
      }

      // Ensure options is always an array
      const safeOptions = Array.isArray(options) ? options : [];

      // displayFallback: show when value exists but no option matches (prevents "prepopulated then disappeared")
      let displayFallback: string | undefined;
      const selectedData = fieldName ? formData[`${fieldName}_selectedData`] : undefined;
      if (selectedData) {
        displayFallback =
          (selectedData as any).jobcard_no ??
          (selectedData as any).name ??
          (selectedData as any).label;
      }
      if (!displayFallback && fieldConfig.parentField) {
        const parentSelectedData = formData[`${fieldConfig.parentField}_selectedData`];
        const bindColumn = (fieldConfig.BindParent || fieldConfig.bindParent)?.[fieldConfig.parentField];
        if (parentSelectedData && bindColumn) {
          displayFallback =
            (parentSelectedData as any).insurance_name ??
            (parentSelectedData as any).customername ??
            (parentSelectedData as any).customer_name ??
            (parentSelectedData as any)[`${bindColumn}_name`];
        }
      }

      // Create updated config with fetched options
      const updatedConfig = {
        ...fieldConfig,
        name: fieldName,
        options: safeOptions,
        lookupName: lookupName, // Ensure lookupName is set for SearchableDropdown
        displayFallback,
      };

      // Get value from formData - try both field name and Json_Attribute
      const jsonAttr = fieldConfig.Json_Attribute;
      let fieldValue = formData[fieldName];

      // If value not found by field name, try Json_Attribute
      if (
        (fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === "") &&
        jsonAttr
      ) {
        fieldValue = formData[jsonAttr];
      }

      // Fall back to defaultValue if still no value
      if (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === ""
      ) {
        fieldValue = fieldConfig.defaultValue;
      }


      // Create onFocus handler to fetch lookup if needed when dropdown opens
      const handleFocus = () => {
        if (lookupName) {
          if (fieldConfig.parentField) {
            const parentValue = formData[fieldConfig.parentField];
            if (
              parentValue !== undefined &&
              parentValue !== null &&
              parentValue !== ""
            ) {
              const lookupKey = `${lookupName}_${parentValue}`;
              // Refresh dependent lookup to get latest data, but only if not already loading or requested
              if (
                !lookupsState.loading[lookupKey] &&
                !requestedLookupsRef.current.has(lookupKey)
              ) {
                const currentOptions = lookupsState.lookups[lookupKey] || [];
                if (currentOptions.length === 0) {
                  requestedLookupsRef.current.add(lookupKey);

                  dispatch(
                    fetchDependentLookup({
                      lookupName: lookupName,
                      parentValue,
                      lookuptype: "Select",
                    })
                  )
                    .then(() => {
                      requestedLookupsRef.current.delete(lookupKey);
                    })
                    .catch(() => {
                      requestedLookupsRef.current.delete(lookupKey);
                    });
                }
              }
            }
          } else {
            const lookupKey = lookupName;
            const currentOptions = lookupsState.lookups[lookupKey] || [];
            if (
              currentOptions.length === 0 &&
              !lookupsState.loading[lookupKey] &&
              !requestedLookupsRef.current.has(lookupKey)
            ) {
              requestedLookupsRef.current.add(lookupKey);
              dispatch(
                fetchLookup({
                  lookupName: lookupName,
                  params: {
                    parentid: "",
                    lookuptype: "Select",
                  },
                })
              )
                .then(() => {
                  requestedLookupsRef.current.delete(lookupKey);
                })
                .catch(() => {
                  requestedLookupsRef.current.delete(lookupKey);
                });
            }
          }
        }
      };

      // Use the fieldValue already calculated above (checks both field name and Json_Attribute)
      // If not set above, fall back to defaultValue
      const searchableFieldValue =
        fieldValue !== undefined && fieldValue !== null && fieldValue !== ""
          ? fieldValue
          : fieldConfig.defaultValue || "";

      // Enhanced onChange handler that passes full record data to related components (like Report)
      const handleSearchableDropdownChange = (
        name: string,
        value: string | number | boolean | (string | number | boolean)[]
      ) => {
        // Handle array values - use first element if array is provided
        const actualValue = Array.isArray(value) ? value[0] : value;
        // First, update the form data with the value
        handleFormDataChange(name, actualValue);

        // Debug user selection for Purchase Order vendor dropdown.
        if (
          import.meta.env.DEV &&
          String(screenId) === "130" &&
          String(name) === "drp_supplier"
        ) {
          console.log("[Vendor Prefill Debug][Change]", {
            name,
            incomingValue: value,
            actualValue,
            lookupName,
            optionsCount: safeOptions.length,
            optionSample: safeOptions.slice(0, 3).map((opt: any) => ({
              id: opt?.id,
              value: opt?.value,
              label: opt?.label,
            })),
          });
        }

        // If this is a lookup-based dropdown, get the full selected record and update related components
        if (lookupName && actualValue) {
          try {
            // Get the full selected record from rawData or lookup options
            const lookupKey = lookupName;
            const rawData = lookupsState.rawData[lookupKey] || [];
            const lookupOptions = lookupsState.lookups[lookupKey] || [];

            // Find the full record in rawData (preferred - has all properties)
            let fullRecord: any = null;
            if (rawData.length > 0) {
              fullRecord = rawData.find(
                (item: any) =>
                  item.id === actualValue ||
                  String(item.id) === String(actualValue) ||
                  item.value === actualValue ||
                  String(item.value) === String(actualValue)
              );
            }

            // If not found in rawData, try lookup options (has spread properties)
            if (!fullRecord && lookupOptions.length > 0) {
              const selectedOption = lookupOptions.find(
                (opt: any) =>
                  opt.value === actualValue ||
                  String(opt.value) === String(actualValue) ||
                  opt.id === actualValue ||
                  String(opt.id) === String(actualValue)
              );
              if (selectedOption) {
                // Lookup options have all properties spread, so use it directly
                fullRecord = selectedOption;
              }
            }

            // If we have the full record, update related components (like Report)
            if (fullRecord) {
              // Validate that fullRecord is actually a record object, not a config structure
              if (fullRecord.ControlType || fullRecord.ChildControls || fullRecord.FormName) {

                return; // Don't use it
              }

              // Helper: get attribute value from a record with fallbacks for invoice jobcard keys
              const getAttr = (record: any, bindColumn: string) => {
                let v =
                  record[bindColumn] ||
                  record[bindColumn.toLowerCase()] ||
                  record[bindColumn.toUpperCase()];
                if (v == null && bindColumn === "customertype") v = record.customer_type ?? record.customer_type_id ?? "";
                if (v == null && bindColumn === "insurance_name") {
                  v =
                    record.insurance_id ??
                    record.customer_id ??
                    record.customer_name ??
                    "";
                }
                if (v == null && bindColumn === "invoice_date") v = record.job_date ?? record.date ?? "";
                return v != null ? v : "";
              };

              // Apply a record to BindParent fields and selectedData (used for fullRecord or fetched jobcard)
              const applyRecordToBindParent = (record: any) => {
                if (!record) return;
                const selectedDataKey = `${name}_selectedData`;
                handleFormDataChange(selectedDataKey, record);

                const updateRelatedFields = (comp: ComponentConfig | any) => {
                  if (comp.BindParent && typeof comp.BindParent === "object") {
                    const bindValue = comp.BindValue || comp.name;
                    if (!bindValue) return;
                    const bindColumn = comp.BindParent[name];
                    if (bindColumn) {
                      const attributeValue = getAttr(record, bindColumn);
                      if (attributeValue !== undefined && attributeValue !== null && attributeValue !== "") {
                        handleFormDataChange(bindValue, attributeValue.toString());
                      } else {
                        handleFormDataChange(bindValue, "");
                      }
                    }
                  }
                  if ((comp as any).Fields) (comp as any).Fields.forEach(updateRelatedFields);
                  if (comp.childComponents) comp.childComponents.forEach(updateRelatedFields);
                  if ((comp as any).childControls) (comp as any).childControls.forEach(updateRelatedFields);
                };
                config.forEach(updateRelatedFields);
              };

              const selectedDataKey = `${name}_selectedData`;
              handleFormDataChange(selectedDataKey, fullRecord);

              // Invoice jobcard: if selected option lacks customer fields, fetch full jobcard and prepopulate
              const isInvoiceJobcardLookup =
                name === "parentid" &&
                (
                  lookupKey === "invoice_jobcard" ||
                  lookupKey === "invoice_jobcard_search_Select" ||
                  String(lookupKey).startsWith("invoice_jobcard")
                );
              if (import.meta.env.DEV && isInvoiceJobcardLookup) {
                console.log("[Invoice Jobcard Prefill] Selected parentid option:", {
                  lookupKey,
                  selectedValue: actualValue,
                  fullRecord,
                  hasCustomertype: getAttr(fullRecord, "customertype") !== "",
                  hasInsuranceName: getAttr(fullRecord, "insurance_name") !== "",
                  customertypeCandidates: {
                    customertype: (fullRecord as any)?.customertype,
                    customer_type: (fullRecord as any)?.customer_type,
                    customer_type_id: (fullRecord as any)?.customer_type_id,
                  },
                  customerCandidates: {
                    insurance_name: (fullRecord as any)?.insurance_name,
                    insurance_id: (fullRecord as any)?.insurance_id,
                    customer_id: (fullRecord as any)?.customer_id,
                    customer_name: (fullRecord as any)?.customer_name,
                  },
                  discountCandidates: {
                    discount: (fullRecord as any)?.discount,
                    discount_amount: (fullRecord as any)?.discount_amount,
                  },
                });
              }
              const needsCustomerFields =
                isInvoiceJobcardLookup &&
                (getAttr(fullRecord, "customertype") === "" || getAttr(fullRecord, "insurance_name") === "");
              const jobcardId = fullRecord.id ?? fullRecord.value ?? actualValue;

              if (needsCustomerFields && jobcardId) {
                if (import.meta.env.DEV) {
                  console.log("[Invoice Jobcard Prefill] Fetching full invoice_jobcard record:", {
                    lookupKey,
                    selectedValue: actualValue,
                    jobcardId,
                    reason: "Missing customertype or insurance_name in selected option",
                  });
                }
                apiService
                  .get(`/pabws/utility/invoice_jobcard`, { id: jobcardId }, {
                    includeAuthToken: true,
                    authHeaderType: "Bearer",
                    errorHandling: "return-empty",
                    transformResponse: (raw: any) => {
                      if (!raw) return null;
                      if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) return raw.data[0];
                      if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) return raw.data[0];
                      return raw;
                    },
                  })
                  .then((fetched: any) => {
                    if (fetched && (fetched.id || fetched.jobcard_no)) {
                      if (import.meta.env.DEV) {
                        console.log("[Invoice Jobcard Prefill] Fetched full record:", {
                          jobcardId,
                          fetched,
                          resolved: {
                            customertype:
                              fetched?.customertype ??
                              fetched?.customer_type ??
                              fetched?.customer_type_id,
                            insurance_name:
                              fetched?.insurance_name ??
                              fetched?.insurance_id ??
                              fetched?.customer_id ??
                              fetched?.customer_name,
                            discount:
                              fetched?.discount ??
                              fetched?.discount_amount,
                          },
                        });
                      }
                      dispatch(addLookupItem({ lookupKey: "invoice_jobcard", item: fetched, isRawData: true }));
                      applyRecordToBindParent(fetched);
                    } else {
                      if (import.meta.env.DEV) {
                        console.warn("[Invoice Jobcard Prefill] Full fetch returned empty/invalid, using selected option:", {
                          jobcardId,
                          fetched,
                        });
                      }
                      applyRecordToBindParent(fullRecord);
                    }
                  })
                  .catch((error) => {
                    if (import.meta.env.DEV) {
                      console.warn("[Invoice Jobcard Prefill] Full fetch failed, using selected option:", {
                        jobcardId,
                        error,
                      });
                    }
                    applyRecordToBindParent(fullRecord);
                  });
              } else {
                if (import.meta.env.DEV && isInvoiceJobcardLookup) {
                  console.log("[Invoice Jobcard Prefill] Using selected option directly for BindParent mapping:", {
                    lookupKey,
                    selectedValue: actualValue,
                    resolved: {
                      customertype: getAttr(fullRecord, "customertype"),
                      insurance_name: getAttr(fullRecord, "insurance_name"),
                      discount:
                        (fullRecord as any)?.discount ??
                        (fullRecord as any)?.discount_amount,
                    },
                  });
                }
                // Process BindParent from current fullRecord
                const updateRelatedFields = (comp: ComponentConfig | any) => {
                  if (comp.BindParent && typeof comp.BindParent === "object") {
                    const bindValue = comp.BindValue || comp.name;
                    if (!bindValue) return;
                    const bindColumn = comp.BindParent[name];
                    if (bindColumn) {
                      const attributeValue = getAttr(fullRecord, bindColumn);
                      if (attributeValue !== undefined && attributeValue !== null && attributeValue !== "") {
                        handleFormDataChange(bindValue, attributeValue.toString());
                      } else {
                        handleFormDataChange(bindValue, "");
                      }
                    }
                  }
                  if ((comp as any).Fields) (comp as any).Fields.forEach(updateRelatedFields);
                  if (comp.childComponents) comp.childComponents.forEach(updateRelatedFields);
                  if ((comp as any).childControls) (comp as any).childControls.forEach(updateRelatedFields);
                };
                config.forEach(updateRelatedFields);
              }

              // Update Report components that have this field as Parent_Field
              const updateRelatedReports = (comp: ComponentConfig | any) => {
                if (comp.componentType === ComponentType.REPORT) {
                  const reportConfig = comp as any;
                  const reportParentField = reportConfig.Parent_Field || reportConfig.parentField;
                  if (reportParentField === name) { /* already updated via selectedDataKey */ }
                }
                if ((comp as any).Fields) (comp as any).Fields.forEach(updateRelatedReports);
                if (comp.childComponents) comp.childComponents.forEach(updateRelatedReports);
              };
              config.forEach(updateRelatedReports);
            }
          } catch (error) {

          }
        }
      };

      // Debug logging for Purchase Order vendor prefill (screen 130).
      if (
        import.meta.env.DEV &&
        String(screenId) === "130" &&
        (fieldName === "drp_supplier" || String(jsonAttr) === "supplier_id")
      ) {
        const hasValueMatch = safeOptions.some(
          (opt: any) => String(opt?.value ?? "") === String(searchableFieldValue ?? "")
        );
        const hasIdMatch = safeOptions.some(
          (opt: any) => String(opt?.id ?? "") === String(searchableFieldValue ?? "")
        );
        console.log("[Vendor Prefill Debug][Render]", {
          screenId,
          fieldName,
          jsonAttr,
          lookupName,
          searchableFieldValue,
          rawFieldByName: formData[fieldName],
          rawFieldByJsonAttr: jsonAttr ? formData[jsonAttr] : undefined,
          selectedDataKey: `${fieldName}_selectedData`,
          selectedData: formData[`${fieldName}_selectedData`],
          optionsCount: safeOptions.length,
          hasValueMatch,
          hasIdMatch,
          optionSample: safeOptions.slice(0, 5).map((opt: any) => ({
            id: opt?.id,
            value: opt?.value,
            label: opt?.label,
            name: opt?.name,
            supplier_id: opt?.supplier_id,
            sup_id: opt?.sup_id,
          })),
        });
      }


      return (
        <SearchableDropdown
          key={key}
          config={updatedConfig}
          value={searchableFieldValue}
          onChange={handleSearchableDropdownChange}
          onSearch={(searchTerm) => {
            if (fieldConfig.onSearchAction) {
              handleActionTrigger(fieldConfig.onSearchAction, { searchTerm });
            }
          }}
          onFocus={handleFocus}
          externalError={formErrors[fieldName]}
        />
      );
    }

    if (component.componentType === ComponentType.DATE_FIELD) {
      const fieldConfig = component as any;
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      let fieldValue = formData[fieldName];
      if (
        (fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === "") &&
        fieldConfig.Json_Attribute
      ) {
        fieldValue = formData[fieldConfig.Json_Attribute];
      }

      // Holiday Calendar: restrict Holiday Date to the selected calendar year.
      // Screen 2224 is the "Add Holiday" screen.
      let effectiveConfig = fieldConfig;
      const isHolidayDateField =
        String(screenId) === "2224" && String(fieldName) === "holiday_date";
      if (isHolidayDateField) {
        const rawYear =
          formData?.calendar_year ??
          formData?.calendarYear ??
          formData?.year ??
          (formData?.parent_id_selectedData as any)?.calendar_year ??
          (formData?.parentid_selectedData as any)?.calendar_year;
        const year = rawYear !== undefined && rawYear !== null ? Number(String(rawYear).trim()) : NaN;
        if (!Number.isNaN(year) && year >= 1900 && year <= 3000) {
          effectiveConfig = {
            ...fieldConfig,
            minDate: `${year}-01-01`,
            maxDate: `${year}-12-31`,
          };
        }
      }
      return (
        <DateField
          key={key}
          config={effectiveConfig}
          value={fieldValue || fieldConfig.defaultValue || ""}
          onChange={handleFormDataChange}
          externalError={formErrors[fieldName]}
        />
      );
    }

    if (component.componentType === ComponentType.DATETIME_FIELD) {
      const fieldConfig = component as any;
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      let fieldValue = formData[fieldName];
      if (
        (fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === "") &&
        fieldConfig.Json_Attribute
      ) {
        fieldValue = formData[fieldConfig.Json_Attribute];
      }
      return (
        <DateTimeField
          key={key}
          config={fieldConfig}
          value={fieldValue || fieldConfig.defaultValue || ""}
          onChange={handleFormDataChange}
          externalError={formErrors[fieldName]}
        />
      );
    }

    if (component.componentType === ComponentType.IMAGE_UPLOAD) {
      const fieldConfig = component as any;
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      const jsonAttr = fieldConfig.Json_Attribute;
      let fieldValue = formData[fieldName];

      if (
        (fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === "") &&
        jsonAttr
      ) {
        fieldValue = formData[jsonAttr];
      }

      return (
        <ImageUpload
          key={key}
          config={fieldConfig}
          value={fieldValue}
          onChange={handleFormDataChange}
          onUpload={(files) =>
            handleActionTrigger(fieldConfig.uploadAction, { files })
          }
        />
      );
    }

    if (component.componentType === ComponentType.FILES) {
      const fieldConfig = component as any;
      const fieldName = fieldConfig.name || fieldConfig.BindValue;
      const jsonAttr = fieldConfig.Json_Attribute;
      const value = formData[fieldName] || formData[jsonAttr];
      const mode = isEditMode ? "edit" : "add";

      return (
        <FileUpload
          key={key}
          config={fieldConfig}
          value={value}
          onChange={handleFormDataChange}
          onPendingFile={handlePendingFile}
          mode={mode}
        />
      );
    }

    if (component.componentType === ComponentType.NAV_ITEM) {
      const navConfig = component as any;

      // Determine if this nav item is active
      const isActive = !!(
        (activeView && navConfig.onPressAction === activeView) ||
        (activeRoute && navConfig.route === activeRoute) ||
        (activeView && navConfig.name === activeView)
      );

      return (
        <NavItem
          key={key}
          config={navConfig}
          isActive={isActive}
          onClick={(route, action) => {
            if (action) {
              handleActionTrigger(action, { route });
            } else if (route) {
              // Handle route-based navigation when no action is specified
              handleActionTrigger("navigate", { route });
            }
          }}
        />
      );
    }

    if (component.componentType === ComponentType.COLLAPSIBLE_NAV_ITEM) {
      const collapsibleNavConfig = component as any;

      // Note: Parent menu should NOT be active when submenu items are active
      // Only the currently active submenu item should have active styling

      return (
        <CollapsibleNavItem
          key={key}
          config={collapsibleNavConfig}
          isActive={false}
          onSubItemClick={(route, action) => {
            if (action) {
              handleActionTrigger(action, { route });
            } else if (route) {
              // Handle route-based navigation when no action is specified
              handleActionTrigger("navigate", { route });
            }
          }}
          expandedItems={expandedItems}
          onToggleExpanded={onToggleExpanded}
          activeView={activeView}
          activeRoute={activeRoute}
        />
      );
    }

    if (component.componentType === ComponentType.SUMMARY_LIST) {
      const summaryListConfig = component as SummaryListComponent;

      // Priority: Form.Name > Name > apiname (Form.Name is most specific)
      // Priority: apiname > Form.Name > Name (apiname allows overriding for custom endpoints like screen/...)
      const moduleName =
        summaryListConfig.apiname ||
        summaryListConfig.Form?.Name ||
        summaryListConfig.Name ||
        "";

      // NEW: Support Parent_Field on SUMMARY_LIST
      // When Parent_Field is set, read the parent value from formData
      // and pass it as parentid in the API request
      const parentFieldName = (summaryListConfig as any).Parent_Field || (summaryListConfig as any).parentField;
      const parentFieldValue = parentFieldName ? formData?.[parentFieldName] : undefined;

      const defaultCalendarMonth = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      };
      const payrollMonthForSummaryKey =
        moduleName === "payroll_summary"
          ? String(formData?.payroll_month_filter || "").trim() || defaultCalendarMonth()
          : "";

      // If Parent_Field is configured but no value selected yet, show empty table
      if (parentFieldName && (!parentFieldValue || parentFieldValue === "")) {
        return (
          <DataTable
            key={`${key}-${refreshKey || ""}-no-parent`}
            config={summaryListConfig}
            onActionTrigger={handleActionTrigger}
            data={[]}
          />
        );
      }

      // Create a stable cache key based on config values that affect the fetcher
      // Include moduleName and parentFieldValue in cache key to ensure uniqueness
      const cacheKey = JSON.stringify({
        moduleName: moduleName, // Include resolved module name in cache key
        apiname: summaryListConfig.apiname,
        Name: summaryListConfig.Name,
        FormName: summaryListConfig.Form?.Name,
        View: summaryListConfig.Form?.View,
        Filter: summaryListConfig.Form?.Filter,
        parentFieldValue: parentFieldValue, // Include parent value so fetcher regenerates on change
        payrollMonthForSummaryKey,
      });

      // Get or create dataFetcher with memoization to prevent recreation on every render
      let dataFetcher = dataFetcherCacheRef.current.get(cacheKey);

      if (
        !dataFetcher &&
        (summaryListConfig.apiname ||
          summaryListConfig.Name ||
          summaryListConfig.Form?.Name)
      ) {
        dataFetcher = async (
          params: DataTableFetchParams
        ): Promise<DataTableFetchResponse> => {
          // Use the moduleName already resolved above (from cache key calculation)
          // This ensures consistency between cache key and actual API call
          const moduleName =
            summaryListConfig.apiname ||
            summaryListConfig.Name ||
            summaryListConfig.Form?.Name ||
            "";

          let response;

          // When Parent_Field is configured, use the lookup/utility API endpoint
          // This matches how SUB_MODULE fetches data via LookupName + parentid
          if (parentFieldName && parentFieldValue) {
            try {
              const lookupResponse = await apiService.post<any>(
                `/pabws/utility/${moduleName}`,
                { parentid: parentFieldValue, lookuptype: "Select" }
              );

              // Transform lookup response to DataTableFetchResponse format
              let rawData: any[] = [];
              if (lookupResponse) {
                if (lookupResponse.data && Array.isArray(lookupResponse.data)) {
                  rawData = lookupResponse.data;
                } else if (Array.isArray(lookupResponse)) {
                  rawData = lookupResponse;
                } else if (lookupResponse.data && typeof lookupResponse.data === "string") {
                  // Handle encrypted data - try to parse
                  try {
                    const parsed = JSON.parse(lookupResponse.data);
                    rawData = Array.isArray(parsed) ? parsed : [parsed];
                  } catch {
                    rawData = [];
                  }
                }
              }

              console.log(`[SUMMARY_LIST Parent_Field] Fetched ${rawData.length} items from /pabws/utility/${moduleName} with parentid=${parentFieldValue}`);

              return {
                success: true,
                data: rawData,
                totalcount: rawData.length,
              };
            } catch (err) {
              console.error(`[SUMMARY_LIST Parent_Field] Error fetching from utility API:`, err);
              return { success: false, totalcount: 0, data: [] };
            }
          }

          // Standard SUMMARY_LIST fetch (no Parent_Field)
          // Extract View and Filter from Form config
          const view = summaryListConfig.Form?.View;
          const filter = summaryListConfig.Form?.Filter;

          // Build payload - only include view and filter if they are defined
          const payload: any = {
            ...params,
          };

          // Attendance session screen (2208):
          // when opened as "2208/<attendance_id>" OR opened as a popup with formData.attendance_id,
          // force a scoped session query.
          if (screenId === "2208" && typeof currentView === "string") {
            const parts = currentView.split("/").filter(Boolean);
            const idFromRoute =
              parts.length > 1 ? parts[parts.length - 1] : "";
            const idFromForm =
              formData?.attendance_id ??
              formData?.attendanceId ??
              formData?.attendanceID ??
              "";
            const maybeAttendanceId =
              (idFromRoute && idFromRoute !== "2208" ? idFromRoute : "") ||
              String(idFromForm || "").trim();

            if (maybeAttendanceId && maybeAttendanceId !== "2208") {
              const existingSearch = Array.isArray(payload.search) ? payload.search : [];
              const alreadyHasAttendanceId = existingSearch.some(
                (item: any) => (item?.name || "").toLowerCase() === "attendance_id"
              );
              if (!alreadyHasAttendanceId) {
                existingSearch.push({
                  name: "attendance_id",
                  value: String(maybeAttendanceId),
                  controltype: "TextView",
                });
              }
              payload.search = existingSearch;
            }
          }

          if (moduleName === "payroll_summary") {
            const ym =
              String(formData?.payroll_month_filter || "").trim() ||
              (() => {
                const d = new Date();
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
              })();
            const existingSearch = Array.isArray(payload.search) ? [...payload.search] : [];
            const filtered = existingSearch.filter(
              (item: any) => (item?.name || "").toLowerCase() !== "payroll_month"
            );
            filtered.push({
              name: "payroll_month",
              value: ym,
              controltype: "TextView",
            });
            payload.search = filtered;
          }

          // Only add view and filter if they are explicitly set (not undefined/null/empty)
          if (view) {
            payload.view = view;
          }
          if (filter) {
            payload.filter = filter;
          }
          if (summaryListConfig.Form?.Query) {
            payload.query = summaryListConfig.Form.Query;
          }

          // Check if moduleName starts with "screen/" - if so, use GET request to /pabws/{moduleName}
          if (moduleName.startsWith("screen/")) {
            // For screen endpoints, we pass params as query string
            response = await apiService.get<GenericModuleResponse>(
              `/pabws/${moduleName}`,
              payload
            );
          } else if (moduleName.startsWith("/")) {
            // Support absolute API paths in apiname (e.g. "/g360/hrms/...") for custom endpoints.
            // These endpoints should return GenericModuleResponse-like structure.
            const r: any = await apiService.get<any>(moduleName, payload, {
              includeAuthToken: true,
            });
            if (r && typeof r === "object" && Array.isArray(r.data)) {
              response = {
                success: r.success !== undefined ? Boolean(r.success) : true,
                totalcount: r.totalcount ?? r.data.length,
                data: r.data,
              };
            } else if (Array.isArray(r)) {
              response = { success: true, totalcount: r.length, data: r };
            } else {
              response = { success: false, totalcount: 0, data: [] };
            }
          } else {
            // Default behavior: POST to /pabws/module/{moduleName}
            response = await apiService.getModuleData(moduleName, payload);
          }

          return response || { success: false, totalcount: 0, data: [] };
        };

        // Cache the dataFetcher function
        dataFetcherCacheRef.current.set(cacheKey, dataFetcher);
      }

      return (
        <DataTable
          key={`${key}-${refreshKey || ""}-${parentFieldValue || ""}${
            moduleName === "payroll_summary" ? `-${payrollMonthForSummaryKey}` : ""
          }`}
          config={summaryListConfig}
          onActionTrigger={handleActionTrigger}
          dataFetcher={dataFetcher}
        />
      );
    }

    if (component.componentType === ComponentType.WEB_VIEW) {
      const webViewConfig = component as any;
      return (
        <WebView
          key={key}
          style={webViewConfig.style}
          reportUrl={webViewConfig.reportUrl}
          reportName={webViewConfig.reportName}
        />
      );
    }

    if (component.componentType === ComponentType.SUB_MODULE) {
      const subModuleConfig = component as any;
      // Handle both old format (BindValue) and new format (name)
      const fieldName = subModuleConfig.name || subModuleConfig.BindValue;
      const bindValue = subModuleConfig.BindValue || subModuleConfig.bindValue;
      // Handle both old format (Label) and new format (label)
      const label = subModuleConfig.label || subModuleConfig.Label;

      // CRITICAL: Try both name and BindValue to get SubModule data from formData
      // This ensures data is found regardless of which key was used to store it
      // Some screens use name, others use BindValue - we need to check both
      let subModuleValue = formData[fieldName];
      if ((!subModuleValue || (Array.isArray(subModuleValue) && subModuleValue.length === 0)) && bindValue && bindValue !== fieldName) {
        subModuleValue = formData[bindValue];

      }

      // Fallback to empty array if no data found
      if (!subModuleValue || !Array.isArray(subModuleValue)) {
        subModuleValue = [];
      }

      // Create updated config with normalized properties
      const updatedConfig = {
        ...subModuleConfig,
        name: fieldName,
        label: label,
      };

      // CRITICAL: Ensure all lookups required by the submodule are fetched BEFORE rendering it

      // This prevents "data missing" issues where expressions or dropdowns fail because data isn't ready
      let allLookupsReady = true;
      if (subModuleConfig.childControls && Array.isArray(subModuleConfig.childControls)) {
        for (const control of subModuleConfig.childControls) {
          const controlAny = control as any;
          const lookupName = controlAny.lookupName || controlAny.LookupName;

          if (lookupName) {
            // Exception: Skip loading check for parts_issue_jobcard_purchase
            const isExceptionLookup = lookupName === "parts_issue_jobcard_purchase";

            // Determine lookup key (handling dependent lookups)
            const parentField = controlAny.parentField || controlAny.Parent_Field || subModuleConfig.parentField;
            let lookupKey = lookupName;
            let shouldFetch = true;
            let parentValue = undefined;

            if (parentField) {
              parentValue = formData[parentField];
              if (parentValue !== undefined && parentValue !== null && parentValue !== "") {
                lookupKey = `${lookupName}_${parentValue}`;
              } else {
                // If parent is required but not selected, we don't fetch yet (and don't block rendering)
                shouldFetch = false;
              }
            }

            if (shouldFetch) {
              const currentOptions = lookupsState.lookups[lookupKey] || [];
              const isLoading = lookupsState.loading[lookupKey];
              const isRequested = requestedLookupsRef.current.has(lookupKey);

              // If data is missing and we haven't requested it, start fetching
              if ((!currentOptions || currentOptions.length === 0) && !isLoading && !isRequested) {
                // Don't block rendering for exception lookups
                if (!isExceptionLookup) {
                  allLookupsReady = false;
                }
                requestedLookupsRef.current.add(lookupKey);

                // Dispatch appropriate fetch action ASYNC to avoid updating
                // state during render (React warning: setState in render)
                setTimeout(() => {
                  if (parentField && parentValue) {
                    dispatch(
                      fetchDependentLookup({
                        lookupName: lookupName,
                        parentValue: parentValue,
                        lookuptype: "Select",
                      })
                    )
                      .then(() => {
                        requestedLookupsRef.current.delete(lookupKey);
                      })
                      .catch(() => {
                        requestedLookupsRef.current.delete(lookupKey);
                      });
                  } else {
                    dispatch(
                      fetchLookup({
                        lookupName: lookupName,
                        params: {
                          parentid: "",
                          lookuptype: "Select",
                        },
                      })
                    )
                      .then(() => {
                        requestedLookupsRef.current.delete(lookupKey);
                      })
                      .catch(() => {
                        requestedLookupsRef.current.delete(lookupKey);
                      });
                  }
                }, 0);
              }
              // If data is missing but it's already loading/requested, we still wait
              else if ((!currentOptions || currentOptions.length === 0) && (isLoading || isRequested)) {
                // Don't block rendering for exception lookups
                if (!isExceptionLookup) {
                  allLookupsReady = false;
                }
              }
            }
          }
        }
      }

      if (!allLookupsReady) {
        return (
          <div key={key} className="p-4 text-center text-gray-500 italic border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            Loading {label || "Submodule"} data...
          </div>
        );
      } return (
        <SubModule
          key={key}
          config={updatedConfig}
          value={subModuleValue}
          onChange={handleFormDataChange}
          formData={formData}
          onFormDataChange={handleFormDataChange}
          externalError={formErrors[fieldName]}
        />
      );
    }

    if (component.componentType === ComponentType.MULTI_SUB_LIST) {
      const multiSubListConfig = component as any;
      // Handle both old format (BindValue) and new format (name)
      const fieldName = multiSubListConfig.name || multiSubListConfig.BindValue;
      // Handle both old format (LookupName) and new format (lookupName)
      const lookupName =
        multiSubListConfig.lookupName || multiSubListConfig.LookupName;
      // Get lookup data if lookupName is provided
      let lookupData = multiSubListConfig.lookupData || [];
      if (lookupName) {
        lookupData = lookupsState.lookups[lookupName] || [];
      }

      return (
        <MultiSubList
          key={key}
          config={multiSubListConfig}
          value={formData[fieldName] || []}
          onChange={handleFormDataChange}
          lookupData={lookupData}
        />
      );
    }

    if (component.componentType === ComponentType.MULTI_CHECKBOX) {
      const multiCheckBoxConfig = component as any;
      // Handle both old format (BindValue) and new format (name)
      const fieldName =
        multiCheckBoxConfig.name || multiCheckBoxConfig.BindValue;
      // Handle both old format (LookupName) and new format (lookupName)
      const lookupName =
        multiCheckBoxConfig.lookupName || multiCheckBoxConfig.LookupName;
      // Get options from lookup if lookupName is provided
      let options = multiCheckBoxConfig.options || [];
      if (lookupName) {
        options = lookupsState.lookups[lookupName] || [];
      }

      // Create updated config with fetched options
      const updatedConfig = {
        ...multiCheckBoxConfig,
        options: options,
        lookupName: lookupName,
      };

      return (
        <MultiCheckBox
          key={key}
          config={updatedConfig}
          value={formData[fieldName] || multiCheckBoxConfig.defaultValue || []}
          onChange={handleFormDataChange}
        />
      );
    }

    if (component.componentType === ComponentType.REPORT) {
      const reportConfig = component as any;

      // Find the parent field's lookup name if Parent_Field is specified
      let parentFieldLookupName: string | undefined = undefined;
      const parentField = reportConfig.Parent_Field || reportConfig.parentField;

      if (parentField) {
        // Search the config tree to find the parent field and its lookup name
        const findParentFieldConfig = (comp: ComponentConfig | any): any => {
          const fieldName = comp.name || comp.BindValue;
          if (fieldName === parentField) {
            return comp;
          }
          if ((comp as any).Fields) {
            for (const field of (comp as any).Fields) {
              const found = findParentFieldConfig(field);
              if (found) return found;
            }
          }
          if (comp.childComponents) {
            for (const child of comp.childComponents) {
              const found = findParentFieldConfig(child);
              if (found) return found;
            }
          }
          if ((comp as any).childControls) {
            for (const child of (comp as any).childControls) {
              const found = findParentFieldConfig(child);
              if (found) return found;
            }
          }
          return null;
        };

        const parentFieldConfig = findParentFieldConfig(config);
        if (parentFieldConfig) {
          parentFieldLookupName = parentFieldConfig.LookupName || parentFieldConfig.lookupName;
        }
      }

      return (
        <Report
          key={key}
          config={reportConfig}
          formData={formData}
          parentFieldLookupName={parentFieldLookupName}
        />
      );
    }

    if (component.componentType === ComponentType.CUSTOM) {
      const customConfig = component as any;
      if (customConfig.customType === "monthFilter") {
        return (
          <MonthFilter
            key={key}
            config={customConfig}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            name={customConfig.name}
            filterField={customConfig.props?.filterField}
          />
        );
      }

      if (customConfig.customType === "profileForm") {
        return (
          <ProfileAddTabs
            key={key}
            {...(customConfig.props || {})}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onActionTrigger={handleActionTrigger}
            isEditMode={!!formData.id}
          />
        );
      }

      return (
        <div
          key={key}
          className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 text-center"
        >
          Custom component: {customConfig.customType}
        </div>
      );
    }

    // Profile Components
    if (component.componentType === ComponentType.PROFILE_HEADER_CARD) {
      return (
        <ProfileHeaderCard
          key={key}
          component={component as ProfileHeaderCardComponent}
          formData={formData}
          handleFormDataChange={handleFormDataChange}
          handleActionTrigger={handleActionTrigger}
        />
      );
    }

    if (component.componentType === ComponentType.PROFILE_CONTACT_INFO) {
      return (
        <ProfileContactInfo
          key={key}
          component={component as ProfileContactInfoComponent}
          formData={formData}
        />
      );
    }

    if (component.componentType === ComponentType.PROFILE_DEPARTMENT_INFO) {
      return (
        <ProfileDepartmentInfo
          key={key}
          component={component as ProfileDepartmentInfoComponent}
          formData={formData}
        />
      );
    }

    if (component.componentType === ComponentType.PROFILE_TABS) {
      return (
        <ProfileTabs
          key={key}
          component={component as ProfileTabsComponent}
          formData={formData}
          handleFormDataChange={handleFormDataChange}
          handleActionTrigger={handleActionTrigger}
        />
      );
    }

    // Fallback for unknown components
    return (
      <div
        key={key}
        className="p-4 border-2 border-dashed border-danger-500 dark:border-danger-500 rounded-lg bg-red-50 dark:bg-red-900/30 text-danger-600 dark:text-danger-400 text-center"
      >
        Unknown component type: {(component as any).componentType}
      </div>
    );
  };

  return (
    <div className="font-sans h-full p-1">
      {config.map((component, index) => renderComponent(component, index))}

      {/* Add CSS for responsive grid with CSS variables */}
      <style>
        {`
          .responsive-grid {
            grid-template-columns: repeat(var(--desktop-cols), minmax(0, 1fr)) !important;
          }
          
          /* Hide empty grid items to prevent unwanted columns */
          .responsive-grid > *:empty:not([class*="px-2"]) {
            display: none !important;
          }
          
          @media (max-width: 1024px) {
            .responsive-grid {
              grid-template-columns: repeat(var(--tablet-cols), minmax(0, 1fr)) !important;
            }
          }
          
          @media (max-width: 768px) {
            .responsive-grid {
              grid-template-columns: repeat(var(--mobile-cols), minmax(0, 1fr)) !important;
            }
          }
        `}
      </style>
      <UnlockModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onSubmit={handleUnlockSubmit}
        isLoading={isUnlocking}
      />
    </div>
  );
};
